import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import { CreditsList, ChargesList, RentalsList, ExtraAssociates } from './components/QualificationFields';
import './App.css';
import { AnalysesPage, AnalysisDetailPage, FinanceDeadlineWidget } from './AnalysesPage';
import { FinancingDossierSection } from './FinancingDossier';
import ProjectNotes from './components/ProjectNotes';
import CoInvestorsSection from './components/CoInvestorsSection';
import NewsletterPage from './NewsletterPage';
import PublicUploadPage from './PublicUploadPage';
import AutoLoginPage from './AutoLoginPage';
import CRMPage from './pages/CRMPage';
import QualificationDetailPage, { QualificationPage } from './pages/QualificationDetailPage';
import ProjectDetailPage from './pages/ProjectDetailPage';
import { AddDelayModal } from './components/AddDelayModal';
import MyTasksWidget from './components/MyTasksWidget';
import MyCockpitWidget from './components/MyCockpitWidget';
import { DraggableSection, useDashboardOrder } from './components/DraggableSection';
import SignContractPage from './SignContractPage';
import ContractsSection from './components/ContractsSection';
import BankScenariosWidget from './components/BankScenariosWidget';
import CeoDashboardPage from './pages/CeoDashboardPage';
import TeamManagementPage from './pages/TeamManagementPage';
import WorksMonthPlanningPanel from './components/WorksMonthPlanningPanel';
import AudioTranscriber from './components/AudioTranscriber';
import CreateClientAccessModal from './components/CreateClientAccessModal';
import ParametresPage from './ParametresPage';
import DeadlinesPage from './DeadlinesPage';
import ActivityFeedPage from './ActivityFeedPage';
import ContractorsPage from './ContractorsPage';
import ClientsDirectoryPage from './ClientsDirectoryPage';
import DocumentsPage from './pages/DocumentsPage';
import FilePreviewModal from './components/FilePreviewModal';
import SearchBar, { matchesSearch } from './components/SearchBar';
import WorksScheduleSection from './components/WorksScheduleSection';
import ClientPortalBudget from './components/ClientPortalBudget';
import ChantierGallery from './components/ChantierGallery';
import ClientPortalAnalyses from './components/ClientPortalAnalyses';
import CourtierPortalPage from './pages/CourtierPortalPage';
import MoePortalPage from './pages/MoePortalPage';
import ArtisanPortalPage from './pages/ArtisanPortalPage';
import { BranchBadge, BranchPageHeader, BranchFilter } from './components/BranchUI';
import GestionLocativePage from './pages/GestionLocativePage';
import GestionPropertyDetailPage from './pages/GestionPropertyDetailPage';
import SignGestionPage from './SignGestionPage';
import ClientPortalGestion from './components/ClientPortalGestion';
import { useAutoSave, AutoSaveIndicator } from './hooks/useAutoSave';
import FinalDossierTab from './FinalDossierTab';
import InstallPWAPrompt from './InstallPWAPrompt';
import BottomNav from './BottomNav';
import TaskToast from './TaskToast';
import { AuthProvider, AuthContext, useAuth, API } from './contexts/AuthContext';

// AuthContext + useAuth sont desormais centralises dans /contexts/AuthContext.js
// On re-exporte useAuth pour la compat ascendante des autres modules qui font import { useAuth } from './App'
export { useAuth };

// ==================== PROTECTED ROUTE ====================
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (allowedRoles && !allowedRoles.includes(user.role) && user.role !== 'admin')
    return <Navigate to="/unauthorized" replace />;
  return children;
};

const LoadingScreen = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <div className="w-12 h-12 border-4 border-t-[#008A8D] border-gray-200 rounded-full animate-spin mx-auto"></div>
      <p className="mt-4 text-gray-600">Chargement...</p>
    </div>
  </div>
);// ==================== AUTH CALLBACK ====================
const AuthCallback = () => {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = React.useRef(false);
  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const hash = window.location.hash;
    const sessionId = hash.match(/session_id=([^&]+)/)?.[1];
    if (sessionId) {
      axios.post(`${API}/auth/google/session`, { session_id: sessionId })
        .then(res => { setUser(res.data); navigate('/dashboard', { replace: true }); })
        .catch(() => navigate('/login?error=auth_failed', { replace: true }));
    } else navigate('/login', { replace: true });
  }, [navigate, setUser]);
  return <LoadingScreen />;
};

// ==================== LOGIN PAGE ====================
const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, loginWithGoogle, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      if (user.role === 'client') navigate('/client', { replace: true });
      else if (user.role === 'courtier') navigate('/courtier', { replace: true });
      else if (user.role === 'moe') navigate('/moe', { replace: true });
      else navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault(); setError(''); setLoading(true);
    try {
      const r = await login(email, password);
      const role = r?.role || r?.user?.role;
      if (role === 'client') navigate('/client');
      else if (role === 'courtier') navigate('/courtier');
      else if (role === 'moe') navigate('/moe');
      else navigate('/dashboard');
    }
    catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === 'string' ? d : Array.isArray(d) ? d.map(e => e.msg || e).join(' ') : 'Erreur de connexion');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      <div className="hidden lg:flex lg:w-1/2 realease-gradient items-center justify-center p-12">
        <div className="max-w-md text-white text-center">
          <img src="/realease-square-beige.jpg" alt="RealEase" className="w-48 h-48 mx-auto mb-6 rounded-2xl shadow-2xl object-contain" />
          <p className="text-lg opacity-90 tracking-widest mb-2">L'IMMOBILIER CLE EN MAIN</p>
          <p className="text-base opacity-80 leading-relaxed mt-4">Gerez vos projets d'investissement immobilier avec simplicite et efficacite.</p>
        </div>
      </div>
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-white">
        <div className="max-w-md w-full">
          <div className="lg:hidden mb-8 text-center">
            <img src="/realease-logo.jpg" alt="RealEase" className="h-14 mx-auto object-contain" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Connexion</h2>
          <p className="text-gray-600 mb-8">Accedez a votre espace de travail</p>
          {error && <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" data-testid="login-error">{error}</div>}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="realease-label">Email</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} className="realease-input" placeholder="votre@email.com" required data-testid="login-email" />
            </div>
            <div>
              <label className="realease-label">Mot de passe</label>
              <input type="password" value={password} onChange={e => setPassword(e.target.value)} className="realease-input" placeholder="••••••••" required data-testid="login-password" />
            </div>
            <button type="submit" disabled={loading} className="w-full realease-btn-primary disabled:opacity-50" data-testid="login-submit">
              {loading ? 'Connexion...' : 'Se connecter'}
            </button>
          </form>
          <div className="mt-6">
            <div className="relative"><div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200"></div></div><div className="relative flex justify-center text-sm"><span className="px-4 bg-white text-gray-500">ou</span></div></div>
            <button onClick={loginWithGoogle} className="mt-6 w-full flex items-center justify-center gap-3 px-6 py-3 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors" data-testid="google-login-btn">
              <svg className="w-5 h-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
              Continuer avec Google
            </button>
          </div>
          <p className="mt-8 text-center text-sm text-gray-500">Equipe RealEase ? Utilisez vos identifiants fournis.</p>
        </div>
      </div>
    </div>
  );
};

// Time ago helper
const timeAgo = (dateStr) => {
  if (!dateStr) return '';
  const now = new Date();
  const date = new Date(dateStr);
  const diff = Math.floor((now - date) / 1000);
  if (diff < 60) return "A l'instant";
  if (diff < 3600) return `Il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `Il y a ${Math.floor(diff / 3600)}h`;
  return `Il y a ${Math.floor(diff / 86400)}j`;
};

// ==================== SIDEBAR / LAYOUT ====================
const RealeaseLogo = ({ white, className = '' }) => (
  <img
    src={white ? '/realease-square-beige.jpg' : '/realease-icon.jpg'}
    alt="RealEase"
    className={`w-10 h-10 object-contain ${white ? 'rounded-md' : ''} ${className}`}
    style={white ? {} : { mixBlendMode: 'multiply' }}
  />
);

const AppLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [notifCount, setNotifCount] = useState(0);
  const [showNotifs, setShowNotifs] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [loadingNotifs, setLoadingNotifs] = useState(false);
  // Sidebar : ouvert par defaut sur desktop (>= 1024px), ferme sur mobile/tablette.
  // Memoise le choix utilisateur dans localStorage pour persister entre les sessions.
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    if (typeof window === 'undefined') return true;
    const saved = localStorage.getItem('realease_sidebar_open');
    if (saved !== null) return saved === '1';
    return window.innerWidth >= 1024;
  });
  useEffect(() => {
    try { localStorage.setItem('realease_sidebar_open', sidebarOpen ? '1' : '0'); } catch {}
  }, [sidebarOpen]);
  // Ferme automatiquement le sidebar lors du changement de page sur mobile (UX standard drawer)
  useEffect(() => {
    if (typeof window !== 'undefined' && window.innerWidth < 1024) {
      setSidebarOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Poll unread count every 30s + au focus de l'onglet
  useEffect(() => {
    const fetchCount = () => {
      axios.get(`${API}/notifications/count`).then(r => setNotifCount(r.data.unread)).catch(() => {});
    };
    fetchCount();
    const interval = setInterval(fetchCount, 30000);
    const onFocus = () => fetchCount();
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(interval); window.removeEventListener('focus', onFocus); };
  }, []);

  // Validation rapide d'une tache depuis la cloche
  const validateTask = async (notif, e) => {
    e.stopPropagation();
    if (!notif.source_id) return;
    try {
      await axios.put(`${API}/tasks/${notif.source_id}`, { done: true });
      // Marque la notif comme lue + retire-la de la liste
      await axios.put(`${API}/notifications/${notif.notif_id}/read`).catch(() => {});
      setNotifs(notifs.filter(n => n.notif_id !== notif.notif_id));
      setNotifCount(c => Math.max(0, c - 1));
    } catch (err) {
      window.alert(err.response?.data?.detail || 'Erreur');
    }
  };

  // Etat pour la mini-modale "Repondre" : { notif_id, task_id, text, mark_done, sending, replies, loadingReplies }
  const [replyState, setReplyState] = useState(null);

  const openReplyModal = async (notif, e) => {
    e.stopPropagation();
    setReplyState({ notif_id: notif.notif_id, task_id: notif.source_id, text: '', mark_done: false, sending: false, replies: [], loadingReplies: true });
    // Charge l'historique des reponses
    try {
      const r = await axios.get(`${API}/tasks/${notif.source_id}/replies`);
      setReplyState(prev => prev ? { ...prev, replies: r.data.replies || [], loadingReplies: false } : prev);
    } catch {
      setReplyState(prev => prev ? { ...prev, loadingReplies: false } : prev);
    }
  };

  const submitReply = async () => {
    if (!replyState || !replyState.text.trim()) return;
    setReplyState({ ...replyState, sending: true });
    try {
      const r = await axios.post(`${API}/tasks/${replyState.task_id}/reply`, {
        message: replyState.text.trim(),
        mark_done: !!replyState.mark_done,
      });
      // Marque la notif comme lue + retire-la si done
      await axios.put(`${API}/notifications/${replyState.notif_id}/read`).catch(() => {});
      if (replyState.mark_done) {
        setNotifs(prev => prev.filter(n => n.notif_id !== replyState.notif_id));
        setNotifCount(c => Math.max(0, c - 1));
        setReplyState(null);
      } else {
        // Ajoute la nouvelle reponse au fil et reset le textarea
        const newReply = r.data?.reply;
        setReplyState(prev => prev ? {
          ...prev,
          text: '',
          sending: false,
          replies: newReply ? [...(prev.replies || []), newReply] : prev.replies,
        } : prev);
      }
    } catch (err) {
      window.alert(err.response?.data?.detail || 'Erreur envoi reponse');
      setReplyState({ ...replyState, sending: false });
    }
  };

  const openNotifs = async () => {
    setShowNotifs(true);
    setLoadingNotifs(true);
    try {
      const r = await axios.get(`${API}/notifications`);
      setNotifs(r.data);
    } catch {} finally { setLoadingNotifs(false); }
  };

  const markRead = async (notifId) => {
    await axios.put(`${API}/notifications/${notifId}/read`).catch(() => {});
    setNotifs(notifs.map(n => n.notif_id === notifId ? { ...n, read: true } : n));
    setNotifCount(c => Math.max(0, c - 1));
  };

  const markAllRead = async () => {
    await axios.post(`${API}/notifications/read-all`).catch(() => {});
    setNotifs(notifs.map(n => ({ ...n, read: true })));
    setNotifCount(0);
  };

  const handleNotifClick = (notif) => {
    if (!notif.read) markRead(notif.notif_id);
    setShowNotifs(false);
    if (notif.link) {
      // Si c'est une URL absolue (mentions @), extrait le pathname pour rester en SPA
      try {
        if (notif.link.startsWith('http')) {
          const u = new URL(notif.link);
          navigate(u.pathname + u.search + u.hash);
        } else {
          navigate(notif.link);
        }
      } catch {
        window.location.href = notif.link;
      }
    }
  };

  const allNav = [
    { path: '/dashboard', label: 'Tableau de bord', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6', roles: ['admin','ceo','carla','julie','amelie','client'] },
    { group: 'CRM & Prospects', icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z', roles: ['admin','ceo','carla','julie','amelie','collaborateur'],
      children: [
        { path: '/crm', label: 'CRM', roles: ['admin','ceo','carla','julie','amelie','collaborateur'] },
        { path: '/admin/pre-qualifications', label: 'Pre-qualifications', roles: ['admin','ceo','carla','julie','amelie','collaborateur'] },
        { path: '/qualification', label: 'Qualification', roles: ['admin','ceo','carla','julie','amelie','collaborateur'] },
        { path: '/annuaire', label: 'Annuaire Clients', roles: ['admin','ceo','carla','julie','amelie','collaborateur'] },
        { path: '/admin/comptes-clients', label: 'Comptes clients', roles: ['admin','ceo','carla','julie','amelie','collaborateur'] },
      ]
    },
    { path: '/analyses', label: 'Analyse projets', icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z', roles: ['admin','ceo','julie','carla','amelie','collaborateur'] },
    { path: '/projects', label: 'Projets', icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4', roles: ['admin','ceo','julie','carla','amelie','collaborateur'] },
    { path: '/delais', label: 'Delais & Relances', icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z', roles: ['admin','ceo','carla','julie','amelie','collaborateur'] },
    { path: '/artisans', label: 'Annuaire Artisans', icon: 'M11.42 15.17L17.25 21A2.652 2.652 0 0021 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655 5.653a2.548 2.548 0 11-3.586-3.586l6.837-5.63m5.108-.233c.55-.164 1.163-.188 1.743-.14a4.5 4.5 0 004.486-6.336l-3.276 3.277a3.004 3.004 0 01-2.25-2.25l3.276-3.276a4.5 4.5 0 00-6.336 4.486c.091 1.076-.071 2.264-.904 2.95l-.102.085m-1.745 1.437L5.909 7.5H4.5L2.25 3.75l1.5-1.5L7.5 4.5v1.409l4.26 4.26m-1.745 1.437l1.745-1.437m6.615 8.206L15.75 15.75M4.867 19.125h.008v.008h-.008v-.008z', roles: ['admin','ceo','carla','julie','amelie','collaborateur'] },
    { path: '/gestion', label: 'Gestion Locative', icon: 'M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z', roles: ['admin','ceo','carla','julie','amelie','collaborateur'] },
    { path: '/newsletter', label: 'Newsletter', icon: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z', roles: ['ceo','admin'] },
    { path: '/parametres', label: 'Parametres', icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z', roles: ['admin','ceo','carla','julie','amelie','collaborateur'] },
    { path: '/direction', label: 'Direction', icon: 'M12 1l3.09 6.26L22 8.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 13.14 2 8.27l6.91-1.01L12 1z', roles: ['ceo', 'admin'], iconFilled: true },
    { path: '/direction/equipe', label: 'Équipe', icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z', roles: ['ceo', 'admin'] },
  ];

  const navItems = allNav.filter(i => {
    if (i.group) {
      // Group visible si au moins 1 enfant l'est
      return i.children.some(c => c.roles.includes(user?.role) || user?.role === 'admin');
    }
    return i.roles.includes(user?.role) || user?.role === 'admin';
  });
  const priorityColors = { critical: 'bg-red-500', high: 'bg-orange-500', medium: 'bg-yellow-500' };

  // Groupes ouverts dans la Sidebar (par label de groupe)
  const [openGroups, setOpenGroups] = useState(() => {
    // Ouvrir automatiquement le groupe qui contient la route active
    const initial = {};
    for (const item of allNav) {
      if (item.group && item.children?.some(c => location.pathname === c.path || location.pathname.startsWith(c.path + '/'))) {
        initial[item.group] = true;
      }
    }
    return initial;
  });
  const toggleGroup = (label) => setOpenGroups(g => ({ ...g, [label]: !g[label] }));

  return (
    <div className="min-h-screen bg-gray-50 flex" data-testid="app-layout">
      {/* Overlay sur mobile quand le sidebar est ouvert */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
          data-testid="sidebar-overlay"
        />
      )}
      <aside
        className={`bg-white border-r border-gray-200 flex flex-col fixed lg:sticky top-0 left-0 self-start max-h-screen overflow-y-auto z-50 transition-all duration-300 ease-in-out
          ${sidebarOpen ? 'w-64 translate-x-0' : 'w-64 -translate-x-full lg:w-0 lg:translate-x-0 lg:overflow-hidden lg:border-r-0'}`}
        data-testid="sidebar"
      >
        <div className="p-5 border-b border-gray-100 flex items-center justify-between bg-gradient-to-br from-[#008A8D]/5 to-transparent">
          <img src="/realease-logo.jpg" alt="RealEase" className="h-14 object-contain" />
          <button
            onClick={() => setSidebarOpen(false)}
            className="p-1.5 rounded-lg hover:bg-gray-100 lg:hidden text-gray-500"
            aria-label="Fermer le menu"
            data-testid="sidebar-close-btn"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {navItems.map(item => {
            if (item.group) {
              const visibleChildren = item.children.filter(c => c.roles.includes(user?.role) || user?.role === 'admin');
              const isOpen = !!openGroups[item.group];
              const hasActiveChild = visibleChildren.some(c => location.pathname === c.path || location.pathname.startsWith(c.path + '/'));
              return (
                <div key={`group-${item.group}`} data-testid={`nav-group-${item.group.toLowerCase().replace(/[^a-z0-9]+/g,'-')}`}>
                  <button
                    type="button"
                    onClick={() => toggleGroup(item.group)}
                    className={`sidebar-link w-full justify-between ${hasActiveChild ? 'active' : ''}`}
                    data-testid={`nav-group-toggle-${item.group.toLowerCase().replace(/[^a-z0-9]+/g,'-')}`}
                  >
                    <span className="flex items-center gap-3">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d={item.icon} /></svg>
                      <span>{item.group}</span>
                    </span>
                    <svg className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                  </button>
                  {isOpen && (
                    <div className="ml-4 mt-1 space-y-1 border-l border-gray-200 pl-3">
                      {visibleChildren.map(child => {
                        const active = location.pathname === child.path || location.pathname.startsWith(child.path + '/');
                        return (
                          <a key={child.path} href={child.path} className={`sidebar-link text-sm ${active ? 'active' : ''}`} data-testid={`nav-${child.path.slice(1).replace(/\//g,'-')}`}>
                            <span>{child.label}</span>
                          </a>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }
            const active = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
            return (
              <a key={item.path} href={item.path} className={`sidebar-link ${active ? 'active' : ''} ${item.iconFilled ? 'sidebar-link-ceo' : ''}`} data-testid={`nav-${item.path.slice(1)}`}>
                <svg className="w-5 h-5" fill={item.iconFilled ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d={item.icon} /></svg>
                <span>{item.label}</span>
                {item.iconFilled && <span className="ml-auto text-[9px] uppercase font-bold tracking-wider px-1.5 py-0.5 bg-[#272526] text-[#ECC19B] rounded">CEO</span>}
              </a>
            );
          })}
        </nav>
        <div className="p-4 border-t border-gray-100">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-[#008A8D]/10 flex items-center justify-center text-[#008A8D] font-semibold">{user?.name?.charAt(0).toUpperCase()}</div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-gray-900 truncate">{user?.name}</p>
              <p className="text-xs text-gray-500 capitalize">{user?.role}</p>
            </div>
          </div>
          <button onClick={logout} className="w-full px-4 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors text-left flex items-center gap-2" data-testid="logout-btn">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
            Deconnexion
          </button>
        </div>
      </aside>
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Top bar with hamburger + notifications bell */}
        <header className="h-14 bg-white border-b border-gray-200 flex items-center justify-between px-4 lg:px-6 shrink-0 sticky top-0 z-30">
          <button
            onClick={() => setSidebarOpen(s => !s)}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-700"
            aria-label={sidebarOpen ? 'Masquer le menu' : 'Ouvrir le menu'}
            data-testid="sidebar-toggle"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d={sidebarOpen ? "M4 6h16M4 12h16M4 18h16" : "M4 6h16M4 12h16M4 18h7"} />
            </svg>
          </button>
          <div className="lg:hidden">
            <img src="/realease-logo.jpg" alt="RealEase" className="h-8 object-contain" />
          </div>
          <div className="relative">
            <button onClick={openNotifs} className="relative p-2 rounded-lg hover:bg-gray-100 transition-colors" data-testid="notifications-bell">
              <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" /></svg>
              {notifCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center" data-testid="notif-count">{notifCount > 9 ? '9+' : notifCount}</span>
              )}
            </button>
          </div>
        </header>
        <main className="flex-1 overflow-auto pb-20 lg:pb-0">{children}</main>
      </div>

      {/* Bottom navigation mobile only (< lg) */}
      <BottomNav user={user} onMoreClick={() => setSidebarOpen(true)} />

      {/* Toasts en temps reel des nouvelles taches assignees */}
      <TaskToast user={user} />

      {/* Notification Panel */}
      {showNotifs && (
        <>
          <div className="fixed inset-0 bg-black/20 z-40" onClick={() => setShowNotifs(false)}></div>
          <div className="fixed right-0 top-0 h-full w-96 bg-white shadow-2xl z-50 flex flex-col animate-slide-in" data-testid="notifications-panel">
            <div className="p-5 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-900">Notifications</h2>
              <div className="flex items-center gap-3">
                {notifCount > 0 && <button onClick={markAllRead} className="text-sm text-[#008A8D] hover:underline" data-testid="mark-all-read">Tout marquer lu</button>}
                <button onClick={() => setShowNotifs(false)} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
              </div>
            </div>
            <div className="flex-1 overflow-auto">
              {loadingNotifs ? (
                <div className="p-8 text-center"><div className="w-6 h-6 border-3 border-t-[#008A8D] border-gray-200 rounded-full animate-spin mx-auto"></div></div>
              ) : notifs.length === 0 ? (
                <div className="p-8 text-center text-gray-400">
                  <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" /></svg>
                  <p className="text-sm">Aucune notification</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {notifs.map(n => {
                    const isMention = n.type === 'mention';
                    const isTask = n.type === 'task_assigned' && n.source_id;
                    const title = isMention ? `${n.author_name} vous a mentionne`
                                : isTask ? `${n.author_name} vous a assigne une tache`
                                : n.title;
                    const message = isMention
                      ? `${n.source_label ? 'dans ' + n.source_label + ' : ' : ''}"${n.text_preview || ''}"`
                      : isTask
                        ? `"${n.text_preview || ''}"${n.source_label ? ' ' + n.source_label : ''}`
                        : n.message;
                    return (
                    <div key={n.notif_id} className={`p-4 hover:bg-gray-50 transition-colors ${!n.read ? 'bg-[#008A8D]/[0.03]' : ''}`} data-testid={`notif-item-${n.notif_id}`}>
                      <button onClick={() => handleNotifClick(n)} className="w-full text-left">
                        <div className="flex items-start gap-3">
                          <span className={`w-2.5 h-2.5 rounded-full mt-1.5 shrink-0 ${n.read ? 'bg-gray-300' : (isMention ? 'bg-[#008A8D]' : isTask ? 'bg-[#A57F4F]' : (priorityColors[n.priority] || 'bg-gray-400'))}`}></span>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm ${!n.read ? 'font-semibold text-gray-900' : 'text-gray-600'}`}>{title}</p>
                            <p className="text-xs text-gray-500 mt-1 line-clamp-2 italic">{message}</p>
                            <p className="text-xs text-gray-400 mt-1">{timeAgo(n.created_at)}</p>
                          </div>
                        </div>
                      </button>
                      {isTask && (
                        <div className="mt-2 ml-5 flex gap-2 flex-wrap">
                          <button onClick={(e) => validateTask(n, e)}
                                  className="text-xs px-2.5 py-1 rounded-lg bg-emerald-500 text-white font-bold hover:bg-emerald-600 flex items-center gap-1"
                                  data-testid={`notif-validate-${n.notif_id}`}>
                            ✓ Valider
                          </button>
                          <button onClick={(e) => openReplyModal(n, e)}
                                  className="text-xs px-2.5 py-1 rounded-lg bg-[#13736D] text-white font-semibold hover:bg-[#0F5C57] flex items-center gap-1"
                                  data-testid={`notif-reply-${n.notif_id}`}>
                            Repondre
                          </button>
                          <button onClick={(e) => { e.stopPropagation(); handleNotifClick(n); }}
                                  className="text-xs px-2.5 py-1 rounded-lg bg-white border border-gray-300 text-gray-700 font-semibold hover:bg-gray-50">
                            Voir
                          </button>
                        </div>
                      )}
                    </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Mini-modale Repondre a une tache */}
      {replyState && (
        <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4" onClick={() => !replyState.sending && setReplyState(null)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="task-reply-modal">
            <h3 className="text-lg font-bold text-gray-900 mb-1">Repondre au demandeur</h3>
            <p className="text-xs text-gray-500 mb-4">Votre message sera envoye en notification + email a la personne qui vous a assigne cette tache.</p>

            {/* Fil de discussion : reponses precedentes */}
            {replyState.loadingReplies ? (
              <p className="text-xs text-gray-400 italic mb-3">Chargement de l'historique...</p>
            ) : replyState.replies && replyState.replies.length > 0 ? (
              <div className="mb-4 border-l-2 border-[#13736D]/30 pl-3 space-y-2 max-h-48 overflow-y-auto" data-testid="task-reply-history">
                <p className="text-[11px] uppercase font-semibold text-gray-500">Fil de discussion ({replyState.replies.length})</p>
                {replyState.replies.map(r => (
                  <div key={r.reply_id} className="bg-gray-50 rounded-lg p-2.5">
                    <div className="flex items-center gap-1.5 text-[11px] text-gray-500 mb-1">
                      <span className="font-semibold text-gray-700">{r.from_user_name}</span>
                      {r.mark_done && <span className="text-emerald-600 font-semibold">a marque la tache terminee</span>}
                      <span className="ml-auto">{new Date(r.created_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                    <p className="text-sm text-gray-800 whitespace-pre-wrap">{r.message}</p>
                  </div>
                ))}
              </div>
            ) : null}

            <textarea
              value={replyState.text}
              onChange={e => setReplyState({ ...replyState, text: e.target.value })}
              placeholder="C'est fait, tu peux continuer..."
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm h-28 focus:outline-none focus:ring-2 focus:ring-[#13736D]"
              data-testid="task-reply-textarea"
              autoFocus
            />
            <label className="flex items-center gap-2 text-sm text-gray-700 mt-3 cursor-pointer">
              <input type="checkbox"
                     checked={!!replyState.mark_done}
                     onChange={e => setReplyState({ ...replyState, mark_done: e.target.checked })}
                     className="w-4 h-4 accent-emerald-500" />
              Marquer la tache comme terminee
            </label>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setReplyState(null)} disabled={replyState.sending}
                      className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50">
                Fermer
              </button>
              <button onClick={submitReply} disabled={replyState.sending || !replyState.text.trim()}
                      className="px-4 py-2 text-sm bg-[#13736D] text-white rounded-lg hover:bg-[#0F5C57] disabled:opacity-50 font-semibold"
                      data-testid="task-reply-submit">
                {replyState.sending ? 'Envoi...' : 'Envoyer la reponse'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== RECAP BUTTON ====================
const RecapButton = () => {
  const [loading, setLoading] = useState(false);
  const [recap, setRecap] = useState(null);
  const handleGenerate = async () => {
    setLoading(true);
    try { const r = await axios.post(`${API}/notifications/weekly-recap`); setRecap(r.data); }
    catch (e) { console.error(e); } finally { setLoading(false); }
  };
  return (
    <div className="flex items-center gap-4">
      {recap && (
        <div className="text-right text-sm">
          <p className="text-gray-900 font-medium">{recap.flagged_projects || 0} projets a traiter</p>
          <p className="text-gray-500 text-xs">{recap.email_sent ? 'Email envoye' : 'Email non configure'}</p>
        </div>
      )}
      <button onClick={handleGenerate} disabled={loading} className="realease-btn-primary text-sm whitespace-nowrap disabled:opacity-50" data-testid="generate-recap-btn">
        {loading ? 'Generation...' : 'Generer le recap'}
      </button>
    </div>
  );
};

// ==================== HEALTH DOT ====================
const HealthDot = ({ status, size = 'w-3 h-3' }) => {
  const colors = { critique: 'bg-red-500', surveiller: 'bg-yellow-500', sain: 'bg-green-500' };
  return <span className={`inline-block ${size} rounded-full ${colors[status] || 'bg-gray-300'}`}></span>;
};

const HealthBadge = ({ score, status }) => {
  const styles = {
    critique: 'bg-red-100 text-red-800 border-red-200',
    surveiller: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    sain: 'bg-green-100 text-green-800 border-green-200',
  };
  const labels = { critique: 'Critique', surveiller: 'A surveiller', sain: 'Sain' };
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border ${styles[status]}`}>
      <HealthDot status={status} size="w-2 h-2" />
      {labels[status]} ({score}/100)
    </span>
  );
};

// ==================== JULIE RECAP BUTTON ====================
const JulieRecapButton = () => {
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const handle = async () => {
    setLoading(true);
    try { await axios.post(`${API}/notifications/daily-julie-recap`); setSent(true); setTimeout(() => setSent(false), 3000); }
    catch (e) { console.error(e); } finally { setLoading(false); }
  };
  return (
    <button onClick={handle} disabled={loading || sent} className={`text-sm px-4 py-2 rounded-lg font-medium transition-colors ${sent ? 'bg-green-100 text-green-700' : 'realease-btn-secondary'} disabled:opacity-70`} data-testid="julie-recap-btn">
      {sent ? 'Recap envoye !' : loading ? 'Envoi...' : 'Envoyer recap par email'}
    </button>
  );
};

// ==================== DASHBOARD ====================
const DEFAULT_DASH_ORDER = ['cockpit', 'tasks', 'critical-delays'];

const DashboardDraggableSections = () => {
  const { order, moveBefore, reset } = useDashboardOrder('realease_dash_order_v1', DEFAULT_DASH_ORDER);
  const [dragging, setDragging] = useState(null);

  const widgets = {
    cockpit: <MyCockpitWidget />,
    tasks: <MyTasksWidget />,
    'critical-delays': <CriticalDelaysWidget />,
  };

  const labels = {
    cockpit: 'Mon cockpit',
    tasks: 'Mes taches',
    'critical-delays': 'Delais critiques',
  };

  return (
    <div className="mb-8" data-testid="dashboard-draggable">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-gray-500">
          <span className="inline-flex items-center gap-1.5">
            <svg className="w-3.5 h-3.5 text-gray-400" fill="currentColor" viewBox="0 0 6 16" aria-hidden="true"><circle cx="1.5" cy="2" r="1.2"/><circle cx="1.5" cy="8" r="1.2"/><circle cx="1.5" cy="14" r="1.2"/><circle cx="4.5" cy="2" r="1.2"/><circle cx="4.5" cy="8" r="1.2"/><circle cx="4.5" cy="14" r="1.2"/></svg>
            Astuce : glissez-deposez la poignee a gauche de chaque encart pour reorganiser votre tableau de bord.
          </span>
        </p>
        <button onClick={reset} className="text-xs text-[#008A8D] hover:underline" data-testid="dash-reset-order">
          Reinitialiser l'ordre
        </button>
      </div>
      <div className="space-y-6">
        {order.map(key => (
          <DraggableSection
            key={key}
            keyId={key}
            onMove={moveBefore}
            dragging={dragging}
            setDragging={setDragging}
            aria-label={labels[key]}
          >
            {widgets[key]}
          </DraggableSection>
        ))}
      </div>
    </div>
  );
};

const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [cockpit, setCockpit] = useState(null);
  const [healthList, setHealthList] = useState(null);

  useEffect(() => {
    axios.get(`${API}/stats/dashboard`).then(r => setStats(r.data)).catch(() => {});
    // Load health data for Julie and CEO
    if (['julie', 'ceo', 'admin'].includes(user?.role)) {
      if (user?.role === 'ceo' || user?.role === 'admin') {
        axios.get(`${API}/health/cockpit`).then(r => setCockpit(r.data)).catch(() => {});
      }
      axios.get(`${API}/health/projects`).then(r => setHealthList(r.data)).catch(() => {});
    }
  }, [user?.role]);

  const roleLabel = { ceo: 'CEO', carla: 'CRM & Qualification', julie: 'Gestion Projets', amelie: 'Gestion Delais', client: 'Client', admin: 'Administrateur' };

  return (
    <div className="p-4 lg:p-8" data-testid="dashboard">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Bonjour, {user?.name}</h1>
        <p className="text-gray-500 mt-1">Role : {roleLabel[user?.role] || user?.role}</p>
      </div>

      {/* === ENCARTS DRAG & DROP : Mon cockpit / Mes taches / Delais critiques === */}
      {['admin','ceo','julie','carla','amelie','collaborateur'].includes(user?.role) && (
        <DashboardDraggableSections />
      )}

      {/* Iter 102 : Planning travaux du mois - widget partage pour tous les collaborateurs internes */}
      {['admin','ceo','julie','carla','amelie','collaborateur'].includes(user?.role) && (
        <div className="mb-6">
          <WorksMonthPlanningPanel />
        </div>
      )}

      {/* === Pilotage detaille (CEO/Julie/Admin uniquement, en accordeon) === */}
      {['admin','ceo','julie'].includes(user?.role) && (
        <details className="mb-8 realease-card" data-testid="advanced-pilotage">
          <summary className="p-4 cursor-pointer flex items-center justify-between hover:bg-gray-50 rounded-t-xl select-none">
            <div className="flex items-center gap-3">
              <svg className="w-5 h-5 text-[#008A8D]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" /></svg>
              <span className="font-semibold text-gray-900">Pilotage detaille (cockpit CEO + sante projets)</span>
            </div>
            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
          </summary>
          <div className="p-6 border-t border-gray-100">

      {/* ===== CEO COCKPIT ===== */}
      {cockpit && (user?.role === 'ceo' || user?.role === 'admin') && (
        <div className="mb-6" data-testid="ceo-cockpit">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="realease-card p-6 border-l-4 border-red-500">
              <p className="text-sm text-gray-500">Projets Critiques</p>
              <p className="text-4xl font-bold text-red-600 mt-1">{cockpit.red_count}</p>
            </div>
            <div className="realease-card p-6 border-l-4 border-yellow-500">
              <p className="text-sm text-gray-500">A surveiller</p>
              <p className="text-4xl font-bold text-yellow-600 mt-1">{cockpit.yellow_count}</p>
            </div>
            <div className="realease-card p-6 border-l-4 border-green-500">
              <p className="text-sm text-gray-500">Sains</p>
              <p className="text-4xl font-bold text-green-600 mt-1">{cockpit.green_count}</p>
            </div>
          </div>

          {cockpit.global_budget_drift_pct > 0 && (
            <div className="realease-card p-4 mb-6 bg-red-50 border border-red-200">
              <p className="text-sm font-medium text-red-800">Derive budget globale : <span className="font-bold">{cockpit.global_budget_drift_pct}%</span></p>
            </div>
          )}

          {cockpit.top5_at_risk?.length > 0 && (
            <div className="realease-card p-6 mb-6" data-testid="top5-risk">
              <h3 className="font-semibold text-gray-900 mb-4">Top 5 projets a risque</h3>
              <div className="space-y-3">
                {cockpit.top5_at_risk.map((p, i) => (
                  <a key={i} href={`/projects/${p.project_id}`} className="flex items-center justify-between p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
                    <div className="flex items-center gap-3">
                      <HealthDot status={p.status} />
                      <div>
                        <p className="font-medium text-gray-900">{p.name}</p>
                        <p className="text-xs text-gray-500">{p.client}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      <span className={`font-bold ${p.score < 40 ? 'text-red-600' : p.score < 70 ? 'text-yellow-600' : 'text-green-600'}`}>{p.score}/100</span>
                      {p.red_delays > 0 && <span className="text-red-600">{p.red_delays} retards</span>}
                      {p.drift > 0 && <span className="text-red-600">+{p.drift}%</span>}
                    </div>
                  </a>
                ))}
              </div>
            </div>
          )}

          <div className="realease-card p-6 mb-8">
            <div className="flex items-center justify-between">
              <div><h2 className="text-lg font-semibold">Recap Hebdomadaire</h2><p className="text-sm text-gray-500 mt-1">Uniquement les projets necessitant une action</p></div>
              <RecapButton />
            </div>
          </div>
        </div>
      )}

      {/* ===== JULIE PILOTAGE VIEW ===== */}
      {healthList && (user?.role === 'julie' || user?.role === 'admin') && (
        <div className="mb-8" data-testid="julie-pilotage">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900">Pilotage Projets</h2>
            <JulieRecapButton />
          </div>
          <div className="realease-card overflow-hidden">
            <table className="realease-table" data-testid="health-table">
              <thead>
                <tr><th>Sante</th><th>Projet</th><th>Client</th><th>Avancement</th><th>Retards</th><th>Derive</th><th>Prochaine action</th></tr>
              </thead>
              <tbody>
                {healthList.slice(0, 20).map(h => (
                  <tr key={h.project_id} className={h.health_status === 'critique' ? 'bg-red-50/50' : ''} data-testid={`health-row-${h.project_id}`}>
                    <td><HealthBadge score={h.health_score} status={h.health_status} /></td>
                    <td><a href={`/projects/${h.project_id}`} className="font-medium text-[#008A8D] hover:underline">{h.name}</a></td>
                    <td className="text-gray-600">{h.client_name}</td>
                    <td>
                      <div className="flex items-center gap-2">
                        <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden"><div className="h-full bg-[#008A8D] rounded-full" style={{width:`${h.progress}%`}}></div></div>
                        <span className="text-sm font-medium">{h.progress}%</span>
                      </div>
                    </td>
                    <td>{h.red_delays_count > 0 ? <span className="text-red-600 font-bold">{h.red_delays_count}</span> : <span className="text-green-600">0</span>}</td>
                    <td className={h.budget_drift_pct > 10 ? 'text-red-600 font-bold' : h.budget_drift_pct > 0 ? 'text-yellow-600' : 'text-green-600'}>{h.budget_drift_pct}%</td>
                    <td className="text-sm text-gray-600 max-w-xs truncate">{h.next_action || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

          </div>
        </details>
      )}

      {/* CRM Stats (Carla) */}
      {stats?.crm && (user?.role === 'carla' || (user?.role === 'ceo' && !cockpit)) && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard title="Nouveaux Leads" value={stats.crm.new_leads} icon="M17 20h5v-2a3 3 0 00-5.356-1.857" color="blue" />
          <StatCard title="En Qualification" value={stats.crm.in_progress} icon="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" color="yellow" />
          <StatCard title="Acceptes" value={stats.crm.accepted} icon="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" color="green" />
          <StatCard title="Taux d'acceptation" value={`${stats.crm.acceptance_rate}%`} icon="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" color="teal" />
        </div>
      )}

      {/* Quick Access */}
      <div className="realease-card p-6">
        <h2 className="text-xl font-semibold mb-4">Acces Rapide</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {(user?.role === 'carla' || user?.role === 'admin' || user?.role === 'ceo') && (
            <QuickLink href="/crm" title="CRM / Pipeline" desc="Gerer les leads et prospects" />
          )}
          {(user?.role === 'julie' || user?.role === 'admin' || user?.role === 'ceo' || user?.role === 'carla') && (
            <QuickLink href="/projects" title="Projets" desc="Suivre les projets clients" />
          )}
          {(user?.role === 'amelie' || user?.role === 'admin' || user?.role === 'ceo') && (
            <QuickLink href="/delais" title="Delais" desc="Gerer les delais et relances" />
          )}
          <QuickLink href="/documents" title="Documents" desc="Gestion documentaire" />
        </div>
      </div>
    </div>
  );
};

const CriticalDelaysWidget = () => {
  const navigate = useNavigate();
  const [delays, setDelays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDelay, setEditDelay] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [projects, setProjects] = useState([]);
  const fetchData = useCallback(async () => {
    try {
      const [r, p] = await Promise.all([
        axios.get(`${API}/delays?alert=red`),
        axios.get(`${API}/projects`),
      ]);
      setDelays((r.data || []).slice(0, 8));
      setProjects(p.data || []);
    } catch {} finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchData(); }, [fetchData]);

  const handleRelance = async (d) => {
    const dest = d.contact_email || d.contact_name || '(aucun interlocuteur externe defini)';
    const note = window.prompt(
      `Relancer "${d.description}" ?\n\nDestinataire : ${dest}\n${d.contact_email ? '> Un email automatique sera envoye.' : '> Aucun email envoye (interlocuteur sans email).'}\n\nNote interne (optionnel) :`,
      ''
    );
    if (note === null) return;
    try {
      const r = await axios.post(`${API}/delays/${d.delay_id}/relance`, { note: note || 'Relance effectuee' });
      const sent = r.data?.email_sent ? `Email envoye a ${dest}` : 'Relance enregistree (pas d\'email envoye)';
      window.alert(sent);
      fetchData();
    } catch (e) {
      window.alert(e?.response?.data?.detail || 'Erreur lors de la relance');
    }
  };

  const handleTerminate = async (d) => {
    if (!window.confirm(`Marquer le delai "${d.description}" comme termine ?`)) return;
    try {
      await axios.put(`${API}/delays/${d.delay_id}`, { status: 'termine' });
      fetchData();
    } catch (e) {
      window.alert(e?.response?.data?.detail || 'Erreur lors de la mise a jour');
    }
  };

  return (
    <div className="realease-card p-6" data-testid="critical-delays-widget">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
          <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M5 19h14a2 2 0 001.84-2.75L13.74 4a2 2 0 00-3.48 0L3.16 16.25A2 2 0 005 19z" /></svg>
          Delais critiques
          <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700">{delays.length}</span>
        </h3>
        <button onClick={() => navigate('/delais')} className="text-[#008A8D] text-sm font-semibold hover:underline">Voir tout &rarr;</button>
      </div>
      <button onClick={() => setShowAdd(true)}
              className="w-full mb-3 px-3 py-2 bg-[#008A8D] text-white text-sm font-semibold rounded-lg hover:bg-[#006B6D] flex items-center justify-center gap-2"
              data-testid="dashboard-new-delay-btn">
        <span className="text-base leading-none">+</span> Nouveau delai
      </button>
      {loading ? <p className="text-xs text-gray-400 italic">Chargement...</p>
       : delays.length === 0 ? <p className="text-xs text-gray-400 italic text-center py-3">Aucun delai critique. Bravo !</p>
       : (
         <div className="space-y-2">
           {delays.map(d => (
             <div key={d.delay_id} className="p-3 border border-red-200 bg-red-50 rounded-lg" data-testid={`critical-delay-${d.delay_id}`}>
               <div className="flex items-start justify-between gap-3">
                 <div className="min-w-0 flex-1">
                   <a href={`/projects/${d.project_id}?tab=delais`} className="block">
                     <div className="flex items-center gap-2">
                       <p className="font-medium text-sm text-red-900 truncate flex-1">{d.description}</p>
                       {d.auto_relance && (
                         <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-white text-[#006B6E] font-semibold whitespace-nowrap" title="Relance automatique activee">↻ AUTO</span>
                       )}
                     </div>
                     <p className="text-xs text-red-700 truncate">{d.project_name} | {d.delay_type}</p>
                   </a>
                   {(d.contact_name || d.contact_email) && (
                     <p className="text-[11px] text-red-800 mt-1 truncate">
                       <span className="font-semibold">Interlocuteur : </span>
                       {d.contact_name || ''}{d.contact_email ? ` (${d.contact_email})` : ''}
                     </p>
                   )}
                 </div>
                 <div className="text-right text-xs flex-shrink-0">
                   <p className="text-red-800 font-bold">{d.target_date}</p>
                   {d.relance_count > 0 && <p className="text-red-600">{d.relance_count} relance(s)</p>}
                 </div>
               </div>
               <div className="flex gap-3 mt-2 pt-2 border-t border-red-200">
                 <button onClick={() => setEditDelay(d)} className="text-xs font-semibold text-gray-700 hover:underline" data-testid={`critical-edit-${d.delay_id}`}>Modifier</button>
                 <button onClick={() => handleRelance(d)} className="text-xs font-semibold text-[#008A8D] hover:underline" data-testid={`critical-relance-${d.delay_id}`}>
                   {d.contact_email ? 'Relancer par email' : 'Relancer'}
                 </button>
                 <button onClick={() => handleTerminate(d)} className="text-xs font-semibold text-green-700 hover:underline ml-auto" data-testid={`critical-terminate-${d.delay_id}`}>
                   ✓ Terminer
                 </button>
               </div>
             </div>
           ))}
         </div>
       )}
      {editDelay && <AddDelayModal projects={projects} editDelay={editDelay} onClose={() => setEditDelay(null)} onSuccess={() => { setEditDelay(null); fetchData(); }} />}
      {showAdd && <AddDelayModal projects={projects} onClose={() => setShowAdd(false)} onSuccess={() => { setShowAdd(false); fetchData(); }} />}
    </div>
  );
};

const StatCard = ({ title, value, icon, color }) => {
  const colors = { blue: 'bg-blue-50 text-blue-600', yellow: 'bg-yellow-50 text-yellow-600', green: 'bg-green-50 text-green-600', teal: 'bg-[#008A8D]/10 text-[#008A8D]', red: 'bg-red-50 text-red-600' };
  return (
    <div className="realease-card p-6 hover-lift">
      <div className="flex items-center justify-between">
        <div><p className="text-sm text-gray-500 mb-1">{title}</p><p className="text-3xl font-bold text-gray-900">{value}</p></div>
        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${colors[color]}`}>
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d={icon} /></svg>
        </div>
      </div>
    </div>
  );
};

const QuickLink = ({ href, title, desc }) => (
  <a href={href} className="flex items-center gap-4 p-4 rounded-lg border border-gray-200 hover:border-[#008A8D] hover:bg-gray-50 transition-all">
    <div className="w-12 h-12 rounded-lg bg-[#008A8D]/10 flex items-center justify-center">
      <svg className="w-6 h-6 text-[#008A8D]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M13 9l3 3m0 0l-3 3m3-3H8m13 0a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
    </div>
    <div><h3 className="font-semibold text-gray-900">{title}</h3><p className="text-sm text-gray-500">{desc}</p></div>
  </a>
);

// ==================== CRM PAGE ====================
// ============== Branch Badges (multi-branches RealEase) ==============
// Composants extraits dans /components/BranchUI.js pour reutilisation

// ==================== QUALIFICATION ====================

// ==================== PROJECTS PAGE (JULIE) ====================
const ProjectsPage = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [branchFilter, setBranchFilter] = useState(() => localStorage.getItem('realease_branch_filter') || 'all');
  const [search, setSearch] = useState('');
  const { user } = useAuth();

  useEffect(() => { localStorage.setItem('realease_branch_filter', branchFilter); }, [branchFilter]);

  const fetchProjects = useCallback(async () => {
    try {
      const q = showArchived ? '?archived=true' : '';
      const r = await axios.get(`${API}/projects${q}`);
      setProjects(r.data);
      setSelectedIds(new Set());
    } catch {} finally { setLoading(false); }
  }, [showArchived]);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const list = (user?.cross_branch_access && branchFilter !== 'all')
      ? projects.filter(p => p.branch === branchFilter) : projects;
    if (selectedIds.size === list.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(list.map(p => p.project_id)));
  };

  const handleBulk = async (action) => {
    if (selectedIds.size === 0) return;
    const ids = Array.from(selectedIds);
    if (action === 'delete' && !window.confirm(`Supprimer definitivement ${ids.length} projet(s) et toutes les donnees associees (delais, messages) ? Cette action est irreversible.`)) return;
    try {
      const r = await axios.post(`${API}/projects/bulk`, { action, project_ids: ids });
      fetchProjects();
      if (r.data?.count !== undefined) alert(`${r.data.count} projet(s) ${action === 'delete' ? 'supprime(s)' : action === 'archive' ? 'archive(s)' : 'restaure(s)'}`);
    } catch (err) { alert(err.response?.data?.detail || 'Erreur'); }
  };

  const handleArchive = async (e, project) => {
    e.preventDefault(); e.stopPropagation();
    try {
      await axios.patch(`${API}/projects/${project.project_id}/archive`);
      fetchProjects();
    } catch (err) { alert(err.response?.data?.detail || 'Erreur'); }
  };

  const handleDelete = async (e, project) => {
    e.preventDefault(); e.stopPropagation();
    if (!window.confirm(`Supprimer definitivement le projet "${project.name}" et toutes ses donnees (delais, messages) ? Cette action est irreversible.`)) return;
    try {
      await axios.delete(`${API}/projects/${project.project_id}`);
      fetchProjects();
    } catch (err) { alert(err.response?.data?.detail || 'Erreur'); }
  };

  const statusLabel = { initiation: 'Initiation', acquisition: 'Acquisition', notaire: 'Notaire', administratif: 'Administratif', travaux: 'Travaux', livraison: 'Livraison', termine: 'Termine' };
  const statusColors = { initiation: 'bg-blue-100 text-blue-800', acquisition: 'bg-purple-100 text-purple-800', notaire: 'bg-indigo-100 text-indigo-800', administratif: 'bg-orange-100 text-orange-800', travaux: 'bg-yellow-100 text-yellow-800', livraison: 'bg-green-100 text-green-800', termine: 'bg-gray-100 text-gray-800' };
  const canManage = ['julie', 'carla', 'admin', 'ceo', 'amelie', 'collaborateur'].includes(user?.role);
  const canDelete = ['julie', 'admin', 'ceo', 'carla', 'amelie', 'collaborateur'].includes(user?.role);

  // Filtrage client-side par branche + recherche libre (visible aux cross-branch users)
  const branchFiltered = (user?.cross_branch_access && branchFilter !== 'all')
    ? projects.filter(p => p.branch === branchFilter)
    : projects;
  const visibleProjects = !search.trim()
    ? branchFiltered
    : branchFiltered.filter(p => matchesSearch(p, search, [
        'name', 'client_name', 'status', 'address', 'city', 'project_id',
      ]));

  return (
    <div className="p-4 lg:p-8" data-testid="projects-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <BranchPageHeader user={user} baseTitle="Projets" />
          <h1 className="text-3xl font-bold text-gray-900">Projets</h1>
          <p className="text-gray-600 mt-1">{showArchived ? 'Projets archives / termines' : 'Suivi des projets clients'}</p>
        </div>
        <div className="flex gap-2 items-center">
          {user?.cross_branch_access && (
            <BranchFilter value={branchFilter} onChange={setBranchFilter} testidPrefix="projects" />
          )}
          {canManage && projects.length > 0 && (
            <label className="flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm cursor-pointer">
              <input type="checkbox" checked={selectedIds.size === visibleProjects.length && visibleProjects.length > 0} onChange={toggleSelectAll} className="w-4 h-4 accent-[#008A8D] cursor-pointer" data-testid="select-all-projects" />
              Tout
            </label>
          )}
          <button onClick={() => setShowArchived(!showArchived)} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${showArchived ? 'bg-[#008A8D] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-testid="toggle-archived-projects">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" /></svg>
            {showArchived ? 'Projets actifs' : 'Archives'}
          </button>
          {!showArchived && (
            <button onClick={() => setShowModal(true)} className="realease-btn-primary flex items-center gap-2" data-testid="add-project-btn">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
              Nouveau Projet
            </button>
          )}
        </div>
      </div>

      {/* Barre de recherche */}
      <SearchBar
        value={search}
        onChange={setSearch}
        placeholder="Rechercher un projet (nom, client, ville, statut)..."
        resultsCount={visibleProjects.length}
        totalCount={branchFiltered.length}
        className="mb-4"
        testid="projects-search" />

      {canManage && selectedIds.size > 0 && (
        <div className="realease-card p-3 mb-4 flex items-center justify-between bg-[#008A8D]/5 border-[#008A8D]/30" data-testid="bulk-action-bar-projects">
          <span className="text-sm font-medium text-[#008A8D]">{selectedIds.size} projet{selectedIds.size > 1 ? 's' : ''} selectionne{selectedIds.size > 1 ? 's' : ''}</span>
          <div className="flex gap-2">
            <button onClick={() => setSelectedIds(new Set())} className="text-sm text-gray-500 hover:text-gray-700 px-3 py-1.5">Annuler</button>
            <button onClick={() => handleBulk(showArchived ? 'unarchive' : 'archive')} className="text-sm bg-gray-200 hover:bg-gray-300 text-gray-800 px-3 py-1.5 rounded-md font-medium" data-testid="bulk-archive-projects">
              {showArchived ? 'Restaurer la selection' : 'Archiver la selection'}
            </button>
            {canDelete && (
              <button onClick={() => handleBulk('delete')} className="text-sm bg-red-500 hover:bg-red-600 text-white px-3 py-1.5 rounded-md font-medium" data-testid="bulk-delete-projects">
                Supprimer la selection
              </button>
            )}
          </div>
        </div>
      )}

      {loading ? <div className="text-center py-8"><div className="w-8 h-8 border-4 border-t-[#008A8D] border-gray-200 rounded-full animate-spin mx-auto"></div></div> : (
        visibleProjects.length === 0 ? (
          <div className="realease-card p-8 text-center text-gray-500" data-testid="no-projects">
            {search.trim()
              ? <>Aucun projet ne correspond a <b>"{search}"</b>. <button onClick={() => setSearch('')} className="text-[#008A8D] hover:underline ml-1">Effacer la recherche</button></>
              : (branchFilter !== 'all' ? `Aucun projet ${branchFilter === 'bretagne' ? 'Bretagne' : 'Nord'}.` : (showArchived ? 'Aucun projet archive.' : 'Aucun projet. Creez-en un depuis le CRM ou manuellement.'))}
          </div>
        ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {visibleProjects.map(p => (
            <div key={p.project_id} className={`realease-card p-6 hover-lift relative group ${selectedIds.has(p.project_id) ? 'ring-2 ring-[#008A8D]' : ''}`} data-testid={`project-card-${p.project_id}`}>
              {canManage && (
                <div className="absolute top-3 left-3 z-10">
                  <input type="checkbox" checked={selectedIds.has(p.project_id)} onChange={() => toggleSelect(p.project_id)} onClick={(e) => e.stopPropagation()} className="w-4 h-4 accent-[#008A8D] cursor-pointer" data-testid={`select-project-${p.project_id}`} />
                </div>
              )}
              <a href={`/projects/${p.project_id}`} className="block pl-6">
                <div className="flex items-start justify-between mb-3">
                  <h3 className="font-semibold text-gray-900 pr-20">{p.name}</h3>
                  <span className={`status-badge ${statusColors[p.status] || 'bg-gray-100'}`}>{statusLabel[p.status]}</span>
                </div>
                {user?.cross_branch_access && p.branch && (
                  <div className="mb-2"><BranchBadge branch={p.branch} /></div>
                )}
                <p className="text-sm text-gray-600 mb-3">{p.client_name}</p>
                <div className="mb-3">
                  <div className="flex justify-between text-xs text-gray-500 mb-1"><span>Avancement</span><span>{p.progress}%</span></div>
                  <div className="progress-bar"><div className="progress-bar-fill" style={{ width: `${p.progress}%` }}></div></div>
                </div>
                {p.budget_planned && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Budget</span>
                    <span className="font-medium">{(p.budget_spent || 0).toLocaleString('fr-FR')} / {p.budget_planned.toLocaleString('fr-FR')} EUR</span>
                  </div>
                )}
                {p.risk_level && p.risk_level !== 'low' && (
                  <div className={`mt-3 text-xs font-medium ${p.risk_level === 'high' ? 'text-red-600' : 'text-yellow-600'}`}>
                    Risque: {p.risk_level === 'high' ? 'Eleve' : 'Moyen'}
                  </div>
                )}
              </a>
              {canManage && (
                <div className="absolute top-3 right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={(e) => handleArchive(e, p)} className="p-1.5 bg-white border border-gray-200 rounded-md text-gray-500 hover:text-gray-800 hover:border-gray-400 shadow-sm" data-testid={`archive-project-${p.project_id}`} title={showArchived ? 'Restaurer' : 'Archiver'}>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" /></svg>
                  </button>
                  {canDelete && (
                    <button onClick={(e) => handleDelete(e, p)} className="p-1.5 bg-white border border-gray-200 rounded-md text-red-500 hover:text-red-700 hover:border-red-400 shadow-sm" data-testid={`delete-project-${p.project_id}`} title="Supprimer">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M8 7V4a2 2 0 012-2h4a2 2 0 012 2v3" /></svg>
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
        )
      )}
      {showModal && <AddProjectModal onClose={() => setShowModal(false)} onSuccess={() => { setShowModal(false); fetchProjects(); }} />}
    </div>
  );
};

// Add Project Modal
export const AddProjectModal = ({ onClose, onSuccess, defaultLeadId, defaultLeadName }) => {
  const [form, setForm] = useState({ name: '', client_lead_id: defaultLeadId || '', client_name: defaultLeadName || '', project_type: '', budget_planned: '', geographic_zone: '', comment: '' });
  const [leads, setLeads] = useState([]);
  const [loadingLeads, setLoadingLeads] = useState(true);
  const up = (k, v) => setForm({ ...form, [k]: v });

  React.useEffect(() => {
    axios.get(`${API}/crm/leads`)
      .then(r => {
        const qualifiable = (r.data || []).filter(l => !l.archived);
        setLeads(qualifiable);
        // Auto-fill client_name si on a un defaultLeadId
        if (defaultLeadId) {
          const matched = qualifiable.find(l => l.lead_id === defaultLeadId);
          if (matched) {
            setForm(f => ({ ...f, client_lead_id: defaultLeadId, client_name: `${matched.first_name || ''} ${matched.last_name || ''}`.trim() }));
          }
        }
      })
      .catch(() => setLeads([]))
      .finally(() => setLoadingLeads(false));
  }, [defaultLeadId]);

  const pickLead = (lead_id) => {
    const lead = leads.find(l => l.lead_id === lead_id);
    if (lead) {
      setForm({
        ...form,
        client_lead_id: lead.lead_id,
        client_name: `${lead.first_name || ''} ${lead.last_name || ''}`.trim() || lead.email || '',
      });
    } else {
      setForm({ ...form, client_lead_id: '', client_name: '' });
    }
  };

  // Auto-save : cree le projet des qu'on a un nom + un client (lead ou saisie manuelle)
  const { status, savedAt, error, recordId } = useAutoSave({
    state: form,
    canCreate: (s) => (s.name?.trim()) && ((s.client_lead_id && s.client_lead_id !== 'manual') || (s.client_lead_id === 'manual' && s.client_name?.trim())),
    createFn: async (s) => {
      const payload = { ...s, budget_planned: s.budget_planned ? parseFloat(s.budget_planned) : null };
      if (payload.client_lead_id === 'manual') payload.client_lead_id = '';
      const r = await axios.post(`${API}/projects`, payload);
      return { id: r.data.project_id, doc: r.data };
    },
    updateFn: async (id, s) => {
      const payload = { ...s, budget_planned: s.budget_planned ? parseFloat(s.budget_planned) : null };
      delete payload.client_lead_id; // ne pas modifier le client_lead_id apres creation via cette voie
      await axios.put(`${API}/projects/${id}`, payload);
    },
    debounceMs: 600,
  });

  const handleClose = () => {
    if (recordId && onSuccess) onSuccess();
    else onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="add-project-modal">
      <div className="bg-white rounded-xl p-6 w-full max-w-lg mx-4 animate-fade-in">
        <div className="flex items-center justify-between mb-2"><h2 className="text-xl font-bold">Nouveau Projet</h2><button onClick={handleClose} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button></div>
        <p className="text-xs text-gray-500 mb-4">Sauvegarde automatique des qu'un client et un nom de projet sont saisis.</p>
        <div className="space-y-4">
          <div>
            <label className="realease-label">Client (qualifie) *</label>
            <select value={form.client_lead_id} onChange={e => pickLead(e.target.value)}
                    className="realease-input" data-testid="project-lead-select" disabled={loadingLeads}>
              <option value="">{loadingLeads ? 'Chargement...' : 'Selectionner un client...'}</option>
              {leads.map(l => (
                <option key={l.lead_id} value={l.lead_id}>
                  {l.first_name} {l.last_name} {l.email ? `(${l.email})` : ''} {l.status === 'qualifie' ? 'OK' : ''}
                </option>
              ))}
              <option value="manual">- Autre (saisir manuellement) -</option>
            </select>
            {form.client_lead_id === 'manual' && (
              <input type="text" value={form.client_name} onChange={e => up('client_name', e.target.value)}
                     className="realease-input mt-2" placeholder="Nom du client" data-testid="project-client-manual" />
            )}
            {form.client_lead_id && form.client_lead_id !== 'manual' && (
              <p className="text-xs text-[#008A8D] mt-1">Lie au lead : {form.client_name}</p>
            )}
          </div>
          <div><label className="realease-label">Nom du projet *</label><input type="text" value={form.name} onChange={e => up('name', e.target.value)} className="realease-input" data-testid="project-name" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div><label className="realease-label">Type</label>
              <select value={form.project_type} onChange={e => up('project_type', e.target.value)} className="realease-input">
                <option value="">Selectionner</option><option value="locatif">Locatif</option><option value="residence">Residence</option><option value="defiscalisation">Defiscalisation</option>
              </select>
            </div>
            <div><label className="realease-label">Budget (EUR)</label><input type="number" value={form.budget_planned} onChange={e => up('budget_planned', e.target.value)} className="realease-input" /></div>
          </div>
          <div><label className="realease-label">Zone geographique</label><input type="text" value={form.geographic_zone} onChange={e => up('geographic_zone', e.target.value)} className="realease-input" /></div>
          <div><label className="realease-label">Commentaire</label><textarea value={form.comment} onChange={e => up('comment', e.target.value)} className="realease-input h-20 resize-none" /></div>
          <div className="flex items-center justify-between pt-4 border-t border-gray-100">
            <AutoSaveIndicator status={status} savedAt={savedAt} error={error} />
            <button type="button" onClick={handleClose}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      recordId ? 'bg-[#008A8D] text-white hover:bg-[#006D72]' : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                    data-testid="project-close-btn">
              {recordId ? 'Fermer' : 'Annuler'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== PROJECT DETAIL ====================
// ==================== UNAUTHORIZED ====================
const UnauthorizedPage = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center"><h1 className="text-4xl font-bold text-gray-900 mb-4">403</h1><p className="text-gray-600 mb-6">Vous n'avez pas acces a cette page.</p><a href="/dashboard" className="realease-btn-primary">Retour</a></div>
  </div>
);


// ==================== CLIENT PORTAL ====================
const ClientPortal = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [previewFile, setPreviewFile] = useState(null);
  const [msgText, setMsgText] = useState('');
  const [sending, setSending] = useState(false);
  const [gestion, setGestion] = useState(null);
  const [tab, setTab] = useState('invest');
  const { user } = useAuth();

  const fetchData = useCallback(async () => {
    try {
      const [r, g] = await Promise.all([
        axios.get(`${API}/portal/my-project`).catch(() => ({ data: null })),
        axios.get(`${API}/gestion/portal-owner`).catch(() => ({ data: { properties: [] } })),
      ]);
      setData(r.data); setGestion(g.data);
      if (!r.data?.project && g.data?.properties?.length > 0) setTab('gestion');
    } catch {} finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchData(); }, [fetchData]);

  const sendMessage = async () => {
    if (!msgText.trim() || !data?.project?.project_id) return;
    setSending(true);
    try {
      await axios.post(`${API}/messages`, { project_id: data.project.project_id, channel: 'client', text: msgText });
      setMsgText('');
      fetchData();
    } catch {} finally { setSending(false); }
  };

  if (loading) return <LoadingScreen />;
  const hasGestion = (gestion?.properties?.length || 0) > 0;
  if (!data?.project && !hasGestion) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center"><p className="text-gray-500 text-lg">Aucun projet associe a votre compte.</p><p className="text-gray-400 text-sm mt-2">Contactez votre conseiller RealEase.</p></div>
    </div>
  );

  const p = data?.project;
  const healthColors = { sain: 'text-green-600', surveiller: 'text-yellow-600', critique: 'text-red-600' };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="client-portal">
      <header className="bg-white border-b border-gray-200 px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3"><img src="/realease-logo.jpg" alt="RealEase" className="h-9 object-contain" /></div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-600">{user?.name}</span>
          <button onClick={() => axios.post(`${API}/auth/logout`).then(() => window.location.href = '/login')} className="text-sm text-gray-500 hover:text-gray-700">Deconnexion</button>
        </div>
      </header>
      <div className="max-w-5xl mx-auto p-8">
        {hasGestion && (
          <div className="flex gap-1 mb-8 border-b border-gray-200" data-testid="portal-tabs">
            <button onClick={() => setTab('invest')} className={`px-5 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === 'invest' ? 'border-[#008A8D] text-[#008A8D]' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-testid="portal-tab-invest">Investissement</button>
            <button onClick={() => setTab('gestion')} className={`px-5 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === 'gestion' ? 'border-[#008A8D] text-[#008A8D]' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-testid="portal-tab-gestion">Gestion Locative</button>
          </div>
        )}
        {tab === 'gestion' && hasGestion ? (
          <ClientPortalGestion data={gestion} />
        ) : !p ? (
          <div className="text-center py-16"><p className="text-gray-500 text-lg">Aucun projet d'investissement associe a votre compte.</p></div>
        ) : (
        <>
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">{p.name}</h1>
          <div className="flex items-center gap-4 mt-2">
            <span className="text-gray-600">{p.status_label}</span>
            <HealthBadge score={p.health_score} status={p.health_status} />
          </div>
        </div>

        {/* Timeline */}
        <div className="realease-card p-6 mb-6" data-testid="client-timeline">
          <h3 className="font-semibold text-gray-900 mb-4">Avancement du projet</h3>
          <div className="flex items-center gap-2 overflow-x-auto pb-2">
            {data.timeline.map((t, i) => (
              <div key={i} className="flex items-center">
                <div className={`flex flex-col items-center ${t.current ? 'scale-110' : ''}`}>
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${t.completed ? 'bg-green-500 text-white' : t.current ? 'bg-[#008A8D] text-white ring-4 ring-[#008A8D]/20' : 'bg-gray-200 text-gray-500'}`}>
                    {t.completed ? '\u2713' : i + 1}
                  </div>
                  <span className={`text-xs mt-1 whitespace-nowrap ${t.current ? 'font-semibold text-[#008A8D]' : 'text-gray-500'}`}>{t.phase}</span>
                </div>
                {i < data.timeline.length - 1 && <div className={`w-8 h-0.5 mx-1 ${t.completed ? 'bg-green-500' : 'bg-gray-200'}`}></div>}
              </div>
            ))}
          </div>
          <div className="mt-4"><div className="flex justify-between text-sm mb-1"><span className="text-gray-600">Progression globale</span><span className="font-bold">{p.progress}%</span></div>
            <div className="progress-bar h-3"><div className="progress-bar-fill" style={{width:`${p.progress}%`}}></div></div>
          </div>
        </div>

        {/* Previsionnel travaux (lecture seule cote client) */}
        <div className="mb-6">
          <WorksScheduleSection projectId={p.project_id} mode="client" />
        </div>

        {/* Budget de votre projet */}
        <div className="mb-6">
          <ClientPortalBudget />
        </div>

        {/* Dossier de presentation (analyses PDF) */}
        <div className="mb-6">
          <ClientPortalAnalyses />
        </div>

        {/* Galerie chronologique des photos de chantier */}
        <ChantierGallery meetingReports={data.meeting_reports || []} drivePhotos={data.photos || []} />

        {/* Comptes rendus de reunion partages par le maitre d'oeuvre */}
        {(data.meeting_reports || []).length > 0 && (
          <div className="realease-card p-6 mb-6" data-testid="client-meeting-reports">
            <h3 className="font-semibold text-gray-900 mb-4">Comptes rendus de réunion</h3>
            <div className="space-y-2">
              {data.meeting_reports.map(cr => (
                <details key={cr.cr_id} className="border border-gray-100 rounded-lg" data-testid={`client-cr-${cr.cr_id}`}>
                  <summary className="flex items-center justify-between px-3 py-2.5 cursor-pointer hover:bg-gray-50 list-none">
                    <span className="text-sm font-medium text-gray-800 truncate">{cr.title}</span>
                    <span className="text-xs text-gray-400 shrink-0 ml-2">{cr.meeting_date ? new Date(cr.meeting_date).toLocaleDateString('fr-FR') : new Date(cr.created_at).toLocaleDateString('fr-FR')}</span>
                  </summary>
                  <div className="px-3 pb-3">
                    {cr.participants && <p className="text-xs text-gray-500 mb-1">Participants : {cr.participants}</p>}
                    <p className="whitespace-pre-wrap text-[13px] text-gray-700 bg-gray-50 rounded p-2.5">{cr.content}</p>
                    {(cr.photos || []).length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {cr.photos.map(ph => (
                          <a key={ph.photo_id} href={`${API}/moe/photos/${ph.photo_id}`} target="_blank" rel="noreferrer">
                            <img src={`${API}/moe/photos/${ph.photo_id}`} alt={ph.filename}
                                 className="w-20 h-20 object-cover rounded-lg border border-gray-200"
                                 data-testid={`client-cr-photo-${ph.photo_id}`} />
                          </a>
                        ))}
                      </div>
                    )}
                    <p className="text-[11px] text-gray-400 mt-1">Rédigé par {cr.author_name}, maître d'œuvre</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Documents */}
          <div className="realease-card p-6" data-testid="client-documents">
            <h3 className="font-semibold text-gray-900 mb-4">Vos documents</h3>
            {data.documents.length > 0 ? (
              <div className="space-y-2">{data.documents.map(doc => {
                // Try to extract a Drive file_id from drive_url (formats: .../file/d/<id>/... or .../open?id=<id>)
                let fileId = doc.file_id || doc.drive_file_id || null;
                if (!fileId && doc.drive_url) {
                  const m = doc.drive_url.match(/\/file\/d\/([^/?&]+)/) || doc.drive_url.match(/[?&]id=([^&]+)/);
                  if (m) fileId = m[1];
                }
                const previewable = !!fileId;
                return (
                  <div key={doc.doc_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <button onClick={() => previewable && setPreviewFile({ file_id: fileId, filename: doc.filename, url: doc.drive_url, mime_type: doc.mime_type, icon: doc.icon })}
                      disabled={!previewable}
                      className="flex items-center gap-3 flex-1 text-left min-w-0 disabled:cursor-default" data-testid={`client-doc-${doc.doc_id}`}>
                      <svg className="w-5 h-5 text-[#008A8D] shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
                      <div className="min-w-0"><p className="text-sm font-medium truncate">{doc.filename}</p>{doc.description && <p className="text-xs text-gray-500 truncate">{doc.description}</p>}</div>
                    </button>
                    <div className="flex items-center gap-3 shrink-0">
                      {previewable && <span className="text-[#008A8D] text-sm font-medium">Apercu</span>}
                      {doc.drive_url && <a href={doc.drive_url} target="_blank" rel="noreferrer" className="text-gray-500 text-xs hover:underline">Drive</a>}
                    </div>
                  </div>
                );
              })}</div>
            ) : <p className="text-gray-400 text-sm">Aucun document partage pour le moment.</p>}

            {/* Photos */}
            {data.photos?.length > 0 && (
              <div className="mt-6">
                <h4 className="font-medium text-gray-700 mb-3">Photos / Videos</h4>
                <div className="grid grid-cols-2 gap-2">
                  {data.photos.map(ph => (
                    <div key={ph.doc_id} className="p-3 bg-gray-50 rounded-lg text-center">
                      <svg className="w-8 h-8 mx-auto text-gray-400 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                      <p className="text-xs text-gray-600">{ph.filename}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Messages */}
          <div className="realease-card p-6 flex flex-col" data-testid="client-messages">
            <h3 className="font-semibold text-gray-900 mb-4">Messagerie</h3>
            <div className="flex-1 space-y-3 max-h-80 overflow-auto mb-4">
              {data.messages.length > 0 ? data.messages.map(m => (
                <div key={m.msg_id} className={`p-3 rounded-lg ${m.sender_role === 'client' ? 'bg-[#008A8D]/10 ml-8' : 'bg-gray-100 mr-8'}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium text-gray-700">{m.sender_name}</span>
                    <span className="text-xs text-gray-400">{timeAgo(m.created_at)}</span>
                  </div>
                  <p className="text-sm text-gray-800">{m.text}</p>
                </div>
              )) : <p className="text-gray-400 text-sm text-center py-8">Aucun message.</p>}
            </div>
            <div className="flex gap-2">
              <input type="text" value={msgText} onChange={e => setMsgText(e.target.value)} onKeyDown={e => e.key === 'Enter' && sendMessage()} className="realease-input flex-1" placeholder="Votre message..." data-testid="client-msg-input" />
              <button onClick={sendMessage} disabled={sending || !msgText.trim()} className="realease-btn-primary px-6 disabled:opacity-50" data-testid="client-msg-send">{sending ? '...' : 'Envoyer'}</button>
            </div>
          </div>
        </div>
        </>
        )}
      </div>
      {previewFile && <FilePreviewModal file={previewFile} onClose={() => setPreviewFile(null)} />}
    </div>
  );
};

// ==================== MESSAGING (TEAM) ====================
const MessagingPage = () => {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [channel, setChannel] = useState('internal');
  const [messages, setMessages] = useState([]);
  const [msgText, setMsgText] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [notifyOption, setNotifyOption] = useState('');

  useEffect(() => { axios.get(`${API}/projects`).then(r => { setProjects(r.data); setLoading(false); }).catch(() => setLoading(false)); }, []);

  const loadMessages = useCallback(async () => {
    if (!selectedProject) return;
    try { const r = await axios.get(`${API}/messages/${selectedProject}?channel=${channel}`); setMessages(r.data); } catch {}
  }, [selectedProject, channel]);

  useEffect(() => { loadMessages(); }, [loadMessages]);

  const sendMessage = async () => {
    if (!msgText.trim() || !selectedProject) return;
    setSending(true);
    try {
      const payload = { project_id: selectedProject, channel, text: msgText };
      if (channel === 'client' && notifyOption) payload.notify = notifyOption;
      await axios.post(`${API}/messages`, payload);
      setMsgText(''); loadMessages();
    }
    catch {} finally { setSending(false); }
  };

  return (
    <div className="p-4 lg:p-8" data-testid="messaging-page">
      <h1 className="text-3xl font-bold text-gray-900 mb-6">Messagerie</h1>
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="space-y-2">
          <h3 className="font-semibold text-gray-700 mb-3">Projets</h3>
          {loading ? <div className="w-6 h-6 border-3 border-t-[#008A8D] border-gray-200 rounded-full animate-spin mx-auto"></div> :
          projects.map(p => (
            <button key={p.project_id} onClick={() => setSelectedProject(p.project_id)}
              className={`w-full text-left p-3 rounded-lg text-sm transition-colors ${selectedProject === p.project_id ? 'bg-[#008A8D] text-white' : 'bg-white border border-gray-200 hover:border-[#008A8D]'}`}>
              <p className="font-medium truncate">{p.name}</p>
            </button>
          ))}
        </div>
        <div className="lg:col-span-3">
          {!selectedProject ? (
            <div className="text-center py-16 text-gray-400"><p>Selectionnez un projet</p></div>
          ) : (
            <div className="realease-card flex flex-col" style={{height:'70vh'}}>
              <div className="flex border-b border-gray-200">
                {[{k:'internal',l:'Interne (equipe)'},{k:'client',l:'Client'}].map(({k,l}) => (
                  <button key={k} onClick={() => setChannel(k)} className={`flex-1 py-3 text-sm font-medium border-b-2 transition-colors ${channel === k ? 'border-[#008A8D] text-[#008A8D]' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-testid={`channel-${k}`}>{l}</button>
                ))}
              </div>
              <div className="flex-1 p-4 space-y-3 overflow-auto">
                {messages.length > 0 ? messages.map(m => (
                  <div key={m.msg_id} className={`flex ${m.sender_role === 'client' ? 'justify-start' : 'justify-end'}`}>
                    <div className={`max-w-[70%] p-3 rounded-lg ${m.sender_role === 'client' ? 'bg-gray-100' : 'bg-[#008A8D]/10'}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-medium">{m.sender_name}</span>
                        <span className="text-xs text-gray-400">{timeAgo(m.created_at)}</span>
                      </div>
                      <p className="text-sm">{m.text}</p>
                    </div>
                  </div>
                )) : <p className="text-center text-gray-400 py-8">Aucun message dans ce canal.</p>}
              </div>
              <div className="p-4 border-t border-gray-200">
                <div className="flex gap-2">
                  <input type="text" value={msgText} onChange={e => setMsgText(e.target.value)} onKeyDown={e => e.key === 'Enter' && sendMessage()} className="realease-input flex-1" placeholder="Votre message..." data-testid="msg-input" />
                  {channel === 'client' && (
                    <select value={notifyOption} onChange={e => setNotifyOption(e.target.value)} className="realease-input w-40 text-sm">
                      <option value="">Sans notification</option>
                      <option value="email">Notifier par email</option>
                      <option value="whatsapp">Notifier par WhatsApp</option>
                    </select>
                  )}
                  <button onClick={sendMessage} disabled={sending || !msgText.trim()} className="realease-btn-primary px-6 disabled:opacity-50" data-testid="msg-send">Envoyer</button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ==================== CLIENT ACCOUNTS PAGE (gestion comptes clients) ====================
const ClientAccountsPage = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editClient, setEditClient] = useState(null);
  const { user } = useAuth();
  const isCeoOrAdmin = ['ceo', 'admin'].includes(user?.role);

  const fetchClients = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/clients`);
      setClients(r.data || []);
    } catch (e) {
      alert(e.response?.data?.detail || 'Erreur de chargement');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchClients(); }, [fetchClients]);

  const handleResetPassword = async (c) => {
    if (!window.confirm(`Generer un NOUVEAU mot de passe pour ${c.email} et l'envoyer par email ?`)) return;
    try {
      const r = await axios.post(`${API}/admin/client-access/${c.user_id}/resend`);
      alert(`Email envoye !\nNouveau mot de passe : ${r.data.password}`);
    } catch (e) { alert(e.response?.data?.detail || 'Erreur'); }
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`SUPPRIMER DEFINITIVEMENT le compte de ${c.email} ?\n\nLes leads/projets associes ne seront PAS supprimes mais dissocies de ce compte.\n\nCette action est irreversible.`)) return;
    if (!window.confirm("Vraiment ? Confirmation finale avant suppression.")) return;
    try {
      await axios.delete(`${API}/admin/clients/${c.user_id}`);
      fetchClients();
    } catch (e) { alert(e.response?.data?.detail || 'Erreur de suppression'); }
  };

  const filtered = clients.filter(c => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (c.email || '').toLowerCase().includes(s)
      || (c.name || '').toLowerCase().includes(s)
      || (c.lead?.first_name || '').toLowerCase().includes(s)
      || (c.lead?.last_name || '').toLowerCase().includes(s);
  });

  return (
    <div className="p-4 lg:p-8" data-testid="client-accounts-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Comptes clients</h1>
          <p className="text-gray-600 mt-1">{clients.length} compte(s) actif(s) - modifier email, mot de passe, ou supprimer</p>
        </div>
        <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Rechercher par nom, email..."
                className="realease-input w-64"
                data-testid="search-clients-input" />
      </div>

      <div className="realease-card overflow-hidden">
        {loading ? (
          <div className="p-8 text-center"><div className="w-8 h-8 border-4 border-t-[#008A8D] border-gray-200 rounded-full animate-spin mx-auto"></div></div>
        ) : filtered.length === 0 ? (
          <p className="p-8 text-center text-gray-400">Aucun compte client trouve.</p>
        ) : (
          <table className="realease-table" data-testid="clients-table">
            <thead><tr>
              <th>Nom</th><th>Email</th><th>Telephone</th><th>Lead</th><th>Projets</th><th>Cree le</th><th>Actions</th>
            </tr></thead>
            <tbody>
              {filtered.map(c => (
                <tr key={c.user_id} data-testid={`client-row-${c.user_id}`}>
                  <td className="font-medium">{c.name || '-'}</td>
                  <td className="text-gray-600">{c.email}</td>
                  <td className="text-sm text-gray-500">{c.lead?.phone || '-'}</td>
                  <td className="text-sm">
                    {c.lead ? (
                      <a href={`/qualification/${c.lead.lead_id}`} className="text-[#008A8D] hover:underline">{c.lead.first_name} {c.lead.last_name}</a>
                    ) : <span className="text-gray-400">-</span>}
                  </td>
                  <td className="text-xs">
                    {c.projects?.length > 0 ? (
                      <div className="flex flex-col gap-0.5">
                        {c.projects.slice(0, 3).map(p => (
                          <a key={p.project_id} href={`/projects/${p.project_id}`}
                              className="text-[#008A8D] hover:underline truncate max-w-[180px]">{p.name}</a>
                        ))}
                        {c.projects.length > 3 && <span className="text-gray-400">+ {c.projects.length - 3} autre(s)</span>}
                      </div>
                    ) : <span className="text-gray-400">Aucun</span>}
                  </td>
                  <td className="text-xs text-gray-500">{c.created_at ? new Date(c.created_at).toLocaleDateString('fr-FR') : '-'}</td>
                  <td>
                    <div className="flex gap-3 flex-wrap">
                      <button onClick={() => setEditClient(c)} className="text-[#008A8D] text-sm hover:underline font-medium" data-testid={`edit-client-${c.user_id}`}>Modifier</button>
                      <button onClick={() => handleResetPassword(c)} className="text-amber-600 text-sm hover:underline font-medium" data-testid={`reset-pwd-${c.user_id}`}>Renvoyer MDP</button>
                      {isCeoOrAdmin && (
                        <button onClick={() => handleDelete(c)} className="text-red-600 text-sm hover:underline font-medium" data-testid={`delete-client-${c.user_id}`}>Supprimer</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {editClient && (
        <EditClientAccountModal
          client={editClient}
          onClose={() => setEditClient(null)}
          onSuccess={() => { setEditClient(null); fetchClients(); }}
        />
      )}
    </div>
  );
};

const EditClientAccountModal = ({ client, onClose, onSuccess }) => {
  const [form, setForm] = useState({
    name: client.name || '',
    email: client.email || '',
    new_password: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const updates = {};
      if (form.name !== client.name) updates.name = form.name;
      if (form.email.trim().toLowerCase() !== client.email.toLowerCase()) updates.email = form.email.trim();
      if (form.new_password) updates.new_password = form.new_password;
      if (Object.keys(updates).length === 0) {
        setError('Rien a modifier');
        setLoading(false);
        return;
      }
      await axios.put(`${API}/admin/clients/${client.user_id}`, updates);
      if (form.new_password) {
        alert(`Compte mis a jour. Nouveau mot de passe : ${form.new_password}\n\nCommuniquez-le au client (il n'est pas envoye automatiquement par email avec ce formulaire - utilisez "Renvoyer MDP" pour cela).`);
      }
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur');
    } finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="edit-client-modal">
      <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4 animate-fade-in">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-xl font-bold">Modifier le compte client</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
        </div>
        {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="realease-label">Nom complet</label>
            <input type="text" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="realease-input" data-testid="edit-client-name" />
          </div>
          <div>
            <label className="realease-label">Email</label>
            <input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="realease-input" required data-testid="edit-client-email" />
            <p className="text-xs text-gray-500 mt-1">Le changement d'email se propage sur le lead et les projets associes.</p>
          </div>
          <div>
            <label className="realease-label">Nouveau mot de passe (laisser vide pour ne pas changer)</label>
            <input type="text" value={form.new_password} onChange={e => setForm({...form, new_password: e.target.value})} className="realease-input" placeholder="Min 6 caracteres" data-testid="edit-client-password" />
            <p className="text-xs text-gray-500 mt-1">Si vous voulez aussi envoyer le mot de passe par email, utilisez plutot "Renvoyer MDP" depuis la liste.</p>
          </div>
          <div className="flex gap-3 pt-3">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50">Annuler</button>
            <button type="submit" disabled={loading} className="flex-1 realease-btn-primary disabled:opacity-50" data-testid="save-edit-client">{loading ? 'Sauvegarde...' : 'Sauvegarder'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ==================== ADMIN PAGE ====================
const AdminPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [permsConfig, setPermsConfig] = useState(null);

  const fetchUsers = useCallback(async () => {
    try {
      const [ur, pr] = await Promise.all([axios.get(`${API}/admin/users`), axios.get(`${API}/admin/permissions`)]);
      setUsers(ur.data); setPermsConfig(pr.data);
    } catch {} finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleDelete = async (userId) => {
    if (!window.confirm('Supprimer cet utilisateur ?')) return;
    try { await axios.delete(`${API}/admin/users/${userId}`); fetchUsers(); } catch {}
  };

  const roleLabels = { admin: 'Admin', ceo: 'CEO', carla: 'CRM', julie: 'Projets', amelie: 'Delais', client: 'Client', collaborateur: 'Collaborateur' };

  return (
    <div className="p-4 lg:p-8" data-testid="admin-page">
      <div className="flex items-center justify-between mb-8">
        <div><h1 className="text-3xl font-bold text-gray-900">Administration</h1><p className="text-gray-600 mt-1">Gestion des utilisateurs et permissions</p></div>
        <button onClick={() => setShowCreate(true)} className="realease-btn-primary flex items-center gap-2" data-testid="create-user-btn">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
          Nouveau collaborateur
        </button>
      </div>
      <div className="realease-card overflow-hidden">
        {loading ? <div className="p-8 text-center"><div className="w-8 h-8 border-4 border-t-[#008A8D] border-gray-200 rounded-full animate-spin mx-auto"></div></div> : (
          <table className="realease-table" data-testid="users-table">
            <thead><tr><th>Nom</th><th>Email</th><th>Role</th><th>Permissions</th><th>Actions</th></tr></thead>
            <tbody>
              {users.map(u => (
                <tr key={u.user_id} data-testid={`user-row-${u.user_id}`}>
                  <td className="font-medium">{u.name}</td>
                  <td className="text-gray-600">{u.email}</td>
                  <td><span className="status-badge bg-[#008A8D]/10 text-[#008A8D]">{roleLabels[u.role] || u.role}</span></td>
                  <td className="text-xs text-gray-500 max-w-xs">{(u.permissions || []).join(', ') || '-'}</td>
                  <td className="flex gap-2">
                    <button onClick={() => setEditUser(u)} className="text-[#008A8D] text-sm hover:underline">Modifier</button>
                    {u.role !== 'admin' && <button onClick={() => handleDelete(u.user_id)} className="text-red-500 text-sm hover:underline">Supprimer</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {showCreate && <CreateUserModal permsConfig={permsConfig} onClose={() => setShowCreate(false)} onSuccess={() => { setShowCreate(false); fetchUsers(); }} />}
      {editUser && <EditUserModal user={editUser} permsConfig={permsConfig} onClose={() => setEditUser(null)} onSuccess={() => { setEditUser(null); fetchUsers(); }} />}
    </div>
  );
};

// Edit User Modal
const EditUserModal = ({ user: editUser, permsConfig, onClose, onSuccess }) => {
  const allPerms = permsConfig?.permissions || {};
  const [form, setForm] = useState({ name: editUser.name, role: editUser.role, permissions: editUser.permissions || [], new_password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const togglePerm = (perm) => {
    setForm(prev => ({ ...prev, permissions: prev.permissions.includes(perm) ? prev.permissions.filter(p => p !== perm) : [...prev.permissions, perm] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true); setError('');
    try {
      const updates = { name: form.name, role: form.role, permissions: form.permissions };
      await axios.put(`${API}/admin/users/${editUser.user_id}`, updates);
      onSuccess();
    } catch (err) { setError(err.response?.data?.detail || 'Erreur'); }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 w-full max-w-lg mx-4 animate-fade-in max-h-[90vh] overflow-auto">
        <div className="flex items-center justify-between mb-6"><h2 className="text-xl font-bold">Modifier {editUser.name}</h2><button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button></div>
        {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div><label className="realease-label">Nom</label><input type="text" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="realease-input" required /></div>
          <div><label className="realease-label">Email</label><input type="email" value={editUser.email} disabled className="realease-input bg-gray-50 text-gray-500" /></div>
          <div><label className="realease-label">Role</label>
            <select value={form.role} onChange={e => setForm({...form, role: e.target.value})} className="realease-input">
              <option value="collaborateur">Collaborateur</option><option value="carla">CRM / Qualification</option><option value="julie">Gestion Projets</option><option value="amelie">Gestion Delais</option><option value="ceo">CEO / Direction</option><option value="admin">Administrateur</option><option value="client">Client</option>
            </select>
          </div>
          <div><label className="realease-label">Permissions</label>
            <div className="grid grid-cols-2 gap-2 mt-1">
              {Object.entries(allPerms).map(([key, label]) => (
                <label key={key} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={form.permissions.includes(key)} onChange={() => togglePerm(key)} className="w-4 h-4 rounded text-[#008A8D]" />
                  {label}
                </label>
              ))}
            </div>
          </div>
          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50">Annuler</button>
            <button type="submit" disabled={loading} className="flex-1 realease-btn-primary disabled:opacity-50">{loading ? 'Enregistrement...' : 'Enregistrer'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

const CreateUserModal = ({ permsConfig, onClose, onSuccess }) => {
  const allPerms = permsConfig?.permissions || {};
  const defaults = permsConfig?.default_by_role || {};
  const [form, setForm] = useState({ email: '', name: '', password: '', role: 'collaborateur', permissions: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const up = (k, v) => setForm({ ...form, [k]: v });

  const handleRoleChange = (role) => {
    up('role', role);
    setForm(prev => ({ ...prev, role, permissions: defaults[role] || [] }));
  };

  const togglePerm = (perm) => {
    setForm(prev => ({ ...prev, permissions: prev.permissions.includes(perm) ? prev.permissions.filter(p => p !== perm) : [...prev.permissions, perm] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true); setError('');
    try { await axios.post(`${API}/admin/users`, form); onSuccess(); }
    catch (err) { setError(err.response?.data?.detail || 'Erreur'); } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 w-full max-w-lg mx-4 animate-fade-in max-h-[90vh] overflow-auto">
        <div className="flex items-center justify-between mb-6"><h2 className="text-xl font-bold">Nouveau collaborateur</h2><button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button></div>
        {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div><label className="realease-label">Nom *</label><input type="text" value={form.name} onChange={e => up('name', e.target.value)} className="realease-input" required data-testid="new-user-name" /></div>
          <div><label className="realease-label">Email *</label><input type="email" value={form.email} onChange={e => up('email', e.target.value)} className="realease-input" required data-testid="new-user-email" /></div>
          <div><label className="realease-label">Mot de passe *</label><input type="password" value={form.password} onChange={e => up('password', e.target.value)} className="realease-input" required data-testid="new-user-password" /></div>
          <div><label className="realease-label">Role</label>
            <select value={form.role} onChange={e => handleRoleChange(e.target.value)} className="realease-input" data-testid="new-user-role">
              <option value="collaborateur">Collaborateur (personnalise)</option>
              <option value="carla">CRM / Qualification</option>
              <option value="julie">Gestion Projets</option>
              <option value="amelie">Gestion Delais</option>
              <option value="ceo">CEO / Direction</option>
            </select>
          </div>
          <div><label className="realease-label">Permissions</label>
            <div className="grid grid-cols-2 gap-2 mt-1">
              {Object.entries(allPerms).map(([key, label]) => (
                <label key={key} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={form.permissions.includes(key)} onChange={() => togglePerm(key)} className="w-4 h-4 rounded text-[#008A8D]" />
                  {label}
                </label>
              ))}
            </div>
          </div>
          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50">Annuler</button>
            <button type="submit" disabled={loading} className="flex-1 realease-btn-primary disabled:opacity-50" data-testid="submit-user">{loading ? 'Creation...' : 'Creer'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ==================== CLIENT REGISTER PAGE ====================
const RegisterClientPage = () => {
  const [invite, setInvite] = useState(null);
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const { setUser } = useAuth();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    if (!token) { setError('Lien invalide'); setLoading(false); return; }
    axios.get(`${API}/invitations/verify/${token}`)
      .then(r => { setInvite({ ...r.data, token }); setName(r.data.client_name); })
      .catch(() => setError('Invitation invalide ou expiree'))
      .finally(() => setLoading(false));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault(); setSubmitting(true); setError('');
    try {
      const r = await axios.post(`${API}/auth/register-client`, { token: invite.token, password, name });
      setUser(r.data);
      navigate('/client', { replace: true });
    } catch (err) { setError(err.response?.data?.detail || 'Erreur'); }
    finally { setSubmitting(false); }
  };

  if (loading) return <LoadingScreen />;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-8" data-testid="register-page">
      <div className="max-w-md w-full">
        <div className="text-center mb-8"><RealeaseLogo className="mx-auto mb-4" /><h1 className="text-2xl font-bold text-[#008A8D] tracking-wider">REALEASE</h1></div>
        {error ? (
          <div className="realease-card p-8 text-center"><p className="text-red-600 mb-4">{error}</p><a href="/login" className="text-[#008A8D] hover:underline">Retour a la connexion</a></div>
        ) : (
          <div className="realease-card p-8">
            <h2 className="text-xl font-bold text-gray-900 mb-2">Creez votre compte</h2>
            <p className="text-gray-600 mb-6">Bienvenue {invite?.client_name}, finalisez votre inscription.</p>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div><label className="realease-label">Email</label><input type="email" value={invite?.email || ''} disabled className="realease-input bg-gray-50" /></div>
              <div><label className="realease-label">Votre nom</label><input type="text" value={name} onChange={e => setName(e.target.value)} className="realease-input" required data-testid="register-name" /></div>
              <div><label className="realease-label">Mot de passe</label><input type="password" value={password} onChange={e => setPassword(e.target.value)} className="realease-input" required minLength={6} data-testid="register-password" /></div>
              <button type="submit" disabled={submitting} className="w-full realease-btn-primary disabled:opacity-50" data-testid="register-submit">{submitting ? 'Creation...' : 'Creer mon compte'}</button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

// ==================== INVITE CLIENT BUTTON ====================

// ==================== APP ROUTER ====================
const AppRouter = () => {
  const location = useLocation();
  const { user } = useAuth();
  if (location.hash?.includes('session_id=')) return <AuthCallback />;

  // Client gets their own portal
  if (user?.role === 'client') {
    return (
      <Routes>
        <Route path="/sign/:token" element={<SignContractPage />} />
        <Route path="/sign-gestion/:token" element={<SignGestionPage />} />
        <Route path="/upload-documents/:token" element={<PublicUploadPage />} />
        <Route path="/auto-login/:token" element={<AutoLoginPage />} />
        <Route path="/client" element={<ProtectedRoute><ClientPortal /></ProtectedRoute>} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterClientPage />} />
        <Route path="/pre-qualification" element={<PreQualificationPage />} />
        <Route path="*" element={<Navigate to="/client" replace />} />
      </Routes>
    );
  }

  // Courtier (Alexandre / Artemis) : portail dedie, isole de l'equipe interne
  if (user?.role === 'courtier') {
    const handleLogout = async () => {
      try { await axios.post(`${API}/auth/logout`); } catch {}
      window.location.href = '/login';
    };
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/courtier" element={<ProtectedRoute><CourtierPortalPage user={user} onLogout={handleLogout} /></ProtectedRoute>} />
        <Route path="*" element={<Navigate to="/courtier" replace />} />
      </Routes>
    );
  }

  // Artisan : portail dedie, isole de l'equipe interne (Iter 87)
  if (user?.role === 'artisan') {
    const handleLogout = async () => {
      try { await axios.post(`${API}/auth/logout`); } catch {}
      window.location.href = '/login';
    };
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/artisan" element={<ProtectedRoute><ArtisanPortalPage user={user} onLogout={handleLogout} /></ProtectedRoute>} />
        <Route path="/artisan/:projectId" element={<ProtectedRoute><ArtisanPortalPage user={user} onLogout={handleLogout} /></ProtectedRoute>} />
        <Route path="*" element={<Navigate to="/artisan" replace />} />
      </Routes>
    );
  }

  // Maitre d'oeuvre : portail dedie (vue client + comptes rendus de reunion)
  if (user?.role === 'moe') {
    const handleLogout = async () => {
      try { await axios.post(`${API}/auth/logout`); } catch {}
      window.location.href = '/login';
    };
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/moe" element={<ProtectedRoute><MoePortalPage user={user} onLogout={handleLogout} /></ProtectedRoute>} />
        <Route path="*" element={<Navigate to="/moe" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/sign/:token" element={<SignContractPage />} />
      <Route path="/sign-gestion/:token" element={<SignGestionPage />} />
      <Route path="/upload-documents/:token" element={<PublicUploadPage />} />
      <Route path="/auto-login/:token" element={<AutoLoginPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterClientPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/unauthorized" element={<UnauthorizedPage />} />
      <Route path="/dashboard" element={<ProtectedRoute><AppLayout><Dashboard /></AppLayout></ProtectedRoute>} />
      <Route path="/crm" element={<ProtectedRoute allowedRoles={['carla','ceo','julie','amelie','collaborateur']}><AppLayout><CRMPage /></AppLayout></ProtectedRoute>} />
      <Route path="/qualification" element={<ProtectedRoute allowedRoles={['carla','ceo','julie','amelie','collaborateur']}><AppLayout><QualificationPage /></AppLayout></ProtectedRoute>} />
      <Route path="/qualification/:leadId" element={<ProtectedRoute allowedRoles={['carla','ceo','julie','amelie','collaborateur']}><AppLayout><QualificationDetailPage /></AppLayout></ProtectedRoute>} />
      <Route path="/projects" element={<ProtectedRoute allowedRoles={['julie','ceo','carla','amelie','collaborateur']}><AppLayout><ProjectsPage /></AppLayout></ProtectedRoute>} />
      <Route path="/projects/:projectId" element={<ProtectedRoute allowedRoles={['julie','ceo','carla','amelie','collaborateur']}><AppLayout><ProjectDetailPage /></AppLayout></ProtectedRoute>} />
      <Route path="/analyses" element={<ProtectedRoute allowedRoles={['julie','ceo','carla','amelie','collaborateur']}><AppLayout><AnalysesPage /></AppLayout></ProtectedRoute>} />
      <Route path="/analyses/:analysisId" element={<ProtectedRoute allowedRoles={['julie','ceo','carla','amelie','collaborateur']}><AppLayout><AnalysisDetailPage /></AppLayout></ProtectedRoute>} />
      <Route path="/delays" element={<Navigate to="/delais" replace />} />
      <Route path="/gestion" element={<ProtectedRoute allowedRoles={['carla','julie','amelie','ceo','admin','collaborateur']}><AppLayout><GestionLocativePage /></AppLayout></ProtectedRoute>} />
      <Route path="/gestion/:propertyId" element={<ProtectedRoute allowedRoles={['carla','julie','amelie','ceo','admin','collaborateur']}><AppLayout><GestionPropertyDetailPage /></AppLayout></ProtectedRoute>} />
      <Route path="/documents" element={<ProtectedRoute allowedRoles={['julie','carla','amelie','ceo','collaborateur']}><AppLayout><DocumentsPage /></AppLayout></ProtectedRoute>} />
      <Route path="/messages" element={<ProtectedRoute allowedRoles={['carla','julie','amelie','ceo','collaborateur']}><AppLayout><MessagingPage /></AppLayout></ProtectedRoute>} />
      <Route path="/activity" element={<ProtectedRoute allowedRoles={['carla','julie','amelie','ceo','admin','collaborateur']}><AppLayout><ActivityFeedPage /></AppLayout></ProtectedRoute>} />
      <Route path="/parametres" element={<ProtectedRoute allowedRoles={['carla','julie','amelie','ceo','admin','collaborateur']}><AppLayout><ParametresPage /></AppLayout></ProtectedRoute>} />
      <Route path="/direction" element={<ProtectedRoute allowedRoles={['ceo']}><AppLayout><CeoDashboardPage /></AppLayout></ProtectedRoute>} />
      <Route path="/direction/equipe" element={<ProtectedRoute allowedRoles={['ceo','admin']}><AppLayout><TeamManagementPage /></AppLayout></ProtectedRoute>} />
      <Route path="/newsletter" element={<ProtectedRoute allowedRoles={['ceo','admin']}><AppLayout><NewsletterPage /></AppLayout></ProtectedRoute>} />
      <Route path="/artisans" element={<ProtectedRoute allowedRoles={['carla','julie','amelie','ceo','admin','collaborateur']}><AppLayout><ContractorsPage /></AppLayout></ProtectedRoute>} />
      <Route path="/annuaire" element={<ProtectedRoute allowedRoles={['carla','julie','amelie','ceo','admin','collaborateur']}><AppLayout><ClientsDirectoryPage /></AppLayout></ProtectedRoute>} />
      <Route path="/delais" element={<ProtectedRoute allowedRoles={['carla','julie','amelie','ceo','admin','collaborateur']}><AppLayout><DeadlinesPage /></AppLayout></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute allowedRoles={['admin']}><AppLayout><AdminPage /></AppLayout></ProtectedRoute>} />
      <Route path="/admin/comptes-clients" element={<ProtectedRoute allowedRoles={['carla','julie','amelie','ceo','admin','collaborateur']}><AppLayout><ClientAccountsPage /></AppLayout></ProtectedRoute>} />
      <Route path="/pre-qualification" element={<PreQualificationPage />} />
      <Route path="/booking/:userId" element={<PublicBookingPage />} />
      <Route path="/calendar-settings" element={<ProtectedRoute><AppLayout><CalendarSettingsPage /></AppLayout></ProtectedRoute>} />
      <Route path="/admin/pre-qualifications" element={<ProtectedRoute allowedRoles={['carla','julie','amelie','ceo','admin','collaborateur']}><AppLayout><PreQualAdminPage /></AppLayout></ProtectedRoute>} />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
};

// ==================== PRE-QUALIFICATION (PUBLIC) ====================
// Composant champ reutilisable pour la page de pre-qualification publique.
// Gere text/number/tel/email/select avec un design consistent.
const FField = ({ label, type = 'text', value, onChange, opts = null, placeholder = '', large = false, required = false, error = false }) => {
  const sizeCls = large ? 'px-4 py-3.5 text-base' : 'px-3 py-2 text-sm';
  const borderCls = error ? 'border-red-400 focus:border-red-500 focus:ring-red-200' : 'border-gray-200 focus:border-[#008A8D] focus:ring-[#008A8D]';
  const baseCls = `w-full ${sizeCls} border ${borderCls} rounded-lg focus:outline-none focus:ring-1 bg-white`;
  const labelCls = large
    ? 'block text-sm font-semibold text-gray-700 mb-1.5 tracking-wide'
    : 'block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wide';
  return (
    <label className="block">
      <span className={labelCls}>{label}{required && <span className="text-red-500 ml-0.5">*</span>}</span>
      {type === 'select' ? (
        <select value={value || ''} onChange={e => onChange(e.target.value)} className={baseCls}>
          {(opts || []).map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
        </select>
      ) : type === 'textarea' ? (
        <textarea value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} className={`${baseCls} min-h-[80px]`} />
      ) : (
        <input type={type} value={value ?? ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} className={baseCls} />
      )}
    </label>
  );
};

const PreQualificationPage = () => {
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token');
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submittedId, setSubmittedId] = useState(null);
  const [returnedToken, setReturnedToken] = useState(null);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({
    email: '', first_name: '', last_name: '', phone: '', address: '',
    age: null, family_situation: '', children_count: 0, professional_situation: '',
    monthly_net_income: null, income_stability: '',
    annual_reference_income: null,
    down_payment: null,
    rentals_list: [],
    target_budget: null, geographic_zone: '', horizon: '', objective: '',
    notes: ''
  });

  // Iter 111 : validation bloquante par etape (champs obligatoires demandes par le client)
  // - Etape 0 : email, nom, prenom, telephone, adresse
  // - Etape 2 : revenu fiscal de reference (point bloquant explicite)
  const stepErrors = (s) => {
    const errs = {};
    if (s === 0) {
      if (!form.first_name?.trim()) errs.first_name = 'Prenom requis';
      if (!form.last_name?.trim()) errs.last_name = 'Nom requis';
      if (!form.email?.trim() || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) errs.email = 'Email valide requis';
      if (!form.phone?.trim() || form.phone.replace(/\D/g, '').length < 8) errs.phone = 'Telephone requis';
      if (!form.address?.trim() || form.address.trim().length < 5) errs.address = 'Adresse requise';
    }
    if (s === 2) {
      if (!form.annual_reference_income || +form.annual_reference_income <= 0)
        errs.annual_reference_income = 'Revenu fiscal de reference obligatoire';
    }
    return errs;
  };
  const [showErrors, setShowErrors] = useState(false);
  const currentErrors = stepErrors(step);
  const canAdvance = Object.keys(currentErrors).length === 0;

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/prequalifications/by-token/${token}`)
      .then(r => {
        const d = r.data;
        setForm(prev => ({
          ...prev,
          email: d.email || prev.email,
          first_name: d.first_name || prev.first_name,
          last_name: d.last_name || prev.last_name,
          phone: d.phone || prev.phone,
          address: d.address || prev.address,
          ...(d.data || {})
        }));
        if (d.status === 'submitted' || d.status === 'converted') {
          setSubmittedId(d.prequal_id);
        }
      })
      .catch(() => setError("Lien invalide ou expire"));
  }, [token]);

  const up = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSubmit = async () => {
    setError(null);
    if (!form.email || !form.first_name || !form.last_name) {
      setError("Email, prenom et nom sont obligatoires");
      return;
    }
    setSubmitting(true);
    try {
      const url = token ? `${API}/prequalifications/public-submit?token=${token}` : `${API}/prequalifications/public-submit`;
      const r = await axios.post(url, form);
      setSubmittedId(r.data.prequal_id);
      // Recupere le token (genere par le backend si on n'en avait pas) pour permettre l'upload des docs
      if (r.data.token) setReturnedToken(r.data.token);
      setStep(99);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally { setSubmitting(false); }
  };

  if (error && !submittedId) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <p className="text-red-600 font-medium">{error}</p>
          <p className="text-sm text-gray-500 mt-3">Contactez-nous a contact@real-ease.fr</p>
        </div>
      </div>
    );
  }

  if (submittedId) {
    return <PreQualUploadStep prequalId={submittedId} token={returnedToken || token || ''} firstName={form.first_name} />;
  }

  const stepTitles = ['Vos coordonnees', 'Votre situation', 'Vos revenus', 'Votre projet'];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-gradient-to-br from-[#008A8D] to-[#006B6E] text-white py-10 px-6">
        <div className="max-w-3xl mx-auto flex items-center gap-4">
          <img src="/realease-square-beige.jpg" alt="RealEase" className="w-16 h-16 rounded-lg shadow-lg object-contain" />
          <div>
            <h1 className="text-2xl font-bold tracking-wider">REALEASE</h1>
            <p className="text-sm opacity-90">Pre-qualification de votre projet immobilier</p>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto p-6">
        {/* Iter 111 : laius pour inciter le client a remplir serieusement */}
        <div className="bg-amber-50 border-l-4 border-amber-400 rounded-lg p-4 mb-6 text-sm text-amber-900">
          <p className="font-semibold mb-1">Prenez quelques minutes pour repondre precisement</p>
          <p>Plus votre dossier sera complet et detaille, mieux nous pourrons vous accompagner. Repondez le plus precisement possible aux differentes questions : cela nous permet de commencer a travailler sur votre projet immobilier des reception de votre dossier et de revenir vers vous avec des elements concrets.</p>
        </div>

        <div className="bg-white rounded-xl shadow-md p-8">
          <div className="flex items-center gap-2 mb-8">
            {stepTitles.map((title, i) => (
              <React.Fragment key={i}>
                <div className={`flex-1 text-center ${i === step ? 'text-[#008A8D] font-semibold' : i < step ? 'text-gray-400' : 'text-gray-300'}`}>
                  <div className={`w-8 h-8 mx-auto mb-1 rounded-full flex items-center justify-center text-sm font-bold ${i === step ? 'bg-[#008A8D] text-white' : i < step ? 'bg-gray-200 text-gray-500' : 'bg-gray-100 text-gray-400'}`}>{i < step ? '\u2713' : i + 1}</div>
                  <p className="text-xs hidden sm:block">{title}</p>
                </div>
                {i < stepTitles.length - 1 && <div className="h-px bg-gray-200 flex-1" />}
              </React.Fragment>
            ))}
          </div>

          {step === 0 && (
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Vos coordonnees</h2>
              <p className="text-sm text-gray-500 mb-4">Pour qu'on puisse vous recontacter rapidement.</p>
              <div className="grid grid-cols-2 gap-4">
                <FField label="Prenom" required type="text" value={form.first_name} onChange={v => up('first_name', v)} error={showErrors && !!currentErrors.first_name} />
                <FField label="Nom" required type="text" value={form.last_name} onChange={v => up('last_name', v)} error={showErrors && !!currentErrors.last_name} />
                <FField label="Email" required type="email" value={form.email} onChange={v => up('email', v)} error={showErrors && !!currentErrors.email} />
                <FField label="Telephone" required type="tel" value={form.phone} onChange={v => up('phone', v)} error={showErrors && !!currentErrors.phone} />
                <div className="col-span-2"><FField label="Adresse postale" required type="text" value={form.address} onChange={v => up('address', v)} error={showErrors && !!currentErrors.address} /></div>
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Votre situation</h2>
              <p className="text-sm text-gray-500 mb-4">Quelques infos rapides sur votre profil.</p>
              <div className="grid grid-cols-2 gap-4">
                <FField label="Age" type="number" value={form.age} onChange={v => up('age', parseInt(v) || null)} />
                <FField label="Situation familiale" type="select" value={form.family_situation} onChange={v => up('family_situation', v)} opts={[{v:'',l:'Selectionner'},{v:'celibataire',l:'Celibataire'},{v:'marie',l:'Marie(e)'},{v:'pacse',l:'Pacse(e)'},{v:'divorce',l:'Divorce(e)'}]} />
                <FField label="Nombre d'enfants a charge" type="number" value={form.children_count} onChange={v => up('children_count', parseInt(v) || 0)} />
                <FField label="Situation professionnelle" type="select" value={form.professional_situation} onChange={v => up('professional_situation', v)} opts={[{v:'',l:'Selectionner'},{v:'cdi',l:'CDI'},{v:'fonctionnaire',l:'Fonctionnaire'},{v:'cdd',l:'CDD'},{v:'interim',l:'Interim / Mission'},{v:'independant',l:'Independant / Liberal'},{v:'gerant',l:'Gerant / Chef d\'entreprise'},{v:'retraite',l:'Retraite(e)'},{v:'precaire',l:'Precaire / Sans emploi'}]} />
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Vos revenus</h2>
              <p className="text-sm text-gray-500 mb-4">Indiquez votre revenu net fiscal mensuel et celui des autres investisseurs si vous empruntez a plusieurs.</p>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Investisseur 1 */}
                <div className="p-4 bg-[#008A8D]/5 rounded-lg border-l-4 border-l-[#008A8D]">
                  <h3 className="font-semibold text-gray-800 mb-3">Investisseur 1 (vous)</h3>
                  <div className="space-y-3">
                    <FField large label="Revenu net fiscal mensuel (EUR)" type="number" value={form.monthly_net_income} onChange={v => up('monthly_net_income', parseFloat(v) || null)} placeholder="2 500" />
                    <FField large required label="Revenu fiscal de reference (EUR/an)" type="number" value={form.annual_reference_income} onChange={v => up('annual_reference_income', parseFloat(v) || null)} placeholder="30 000" error={showErrors && !!currentErrors.annual_reference_income} />
                    <FField label="Stabilite des revenus" type="select" value={form.income_stability} onChange={v => up('income_stability', v)} opts={[{v:'',l:'Selectionner'},{v:'cdi',l:'CDI'},{v:'fonctionnaire',l:'Fonctionnaire'},{v:'cdd',l:'CDD'},{v:'interim',l:'Interim / Mission'},{v:'independant',l:'Independant / Liberal'},{v:'gerant',l:'Gerant / Chef d\'entreprise'},{v:'precaire',l:'Precaire / Sans emploi'}]} />
                    <div>
                      <label className="text-xs font-medium text-gray-600 block mb-1">Loyers percus (biens existants)</label>
                      <RentalsList rentals={form.rentals_list || []} onChange={v => up('rentals_list', v)} />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-600 block mb-1">Credits en cours (pour calcul endettement)</label>
                      <CreditsList credits={form.credits || []} onChange={v => up('credits', v)} />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-600 block mb-1">Charges (pension alimentaire, etc.)</label>
                      <ChargesList charges={form.charges_list || []} onChange={v => up('charges_list', v)} />
                    </div>
                  </div>
                </div>

                {/* Investisseur 2 */}
                <div className={`p-4 rounded-lg ${form.has_co_borrower ? 'bg-[#A57F4F]/5 border-l-4 border-l-[#A57F4F]' : 'bg-gray-50 border-l-4 border-l-gray-200'}`}>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-gray-800">Investisseur 2</h3>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={form.has_co_borrower || false} onChange={e => up('has_co_borrower', e.target.checked)} className="w-4 h-4 rounded text-[#008A8D]" data-testid="prequal-has-co-borrower" />
                      <span className="text-xs font-semibold text-gray-700">{form.has_co_borrower ? 'Active' : 'Activer'}</span>
                    </label>
                  </div>
                  {!form.has_co_borrower ? (
                    <p className="text-xs text-gray-400 italic text-center py-8">Si vous empruntez a deux, cochez "Activer".</p>
                  ) : (
                    <div className="space-y-3">
                      <FField label="Nom de l'investisseur 2" type="text" value={form.co_borrower_name} onChange={v => up('co_borrower_name', v)} />
                      <FField label="Lien (conjoint, associe...)" type="text" value={form.co_borrower_relation} onChange={v => up('co_borrower_relation', v)} />
                      <FField large label="Revenu net fiscal mensuel (EUR)" type="number" value={form.co_borrower_income} onChange={v => up('co_borrower_income', parseFloat(v) || null)} placeholder="2 500" />
                      <FField large label="Revenu fiscal de reference (EUR/an)" type="number" value={form.co_borrower_annual_reference_income} onChange={v => up('co_borrower_annual_reference_income', parseFloat(v) || null)} placeholder="30 000" />
                      <FField label="Stabilite des revenus" type="select" value={form.co_borrower_stability} onChange={v => up('co_borrower_stability', v)} opts={[{v:'',l:'Selectionner'},{v:'cdi',l:'CDI'},{v:'fonctionnaire',l:'Fonctionnaire'},{v:'cdd',l:'CDD'},{v:'interim',l:'Interim / Mission'},{v:'independant',l:'Independant / Liberal'},{v:'gerant',l:'Gerant / Chef d\'entreprise'},{v:'precaire',l:'Precaire / Sans emploi'}]} />
                      <div>
                        <label className="text-xs font-medium text-gray-600 block mb-1">Loyers percus (biens existants)</label>
                        <RentalsList rentals={form.co_borrower_rentals_list || []} onChange={v => up('co_borrower_rentals_list', v)} />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-600 block mb-1">Credits en cours</label>
                        <CreditsList credits={form.co_borrower_credits_list || []} onChange={v => up('co_borrower_credits_list', v)} />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-600 block mb-1">Charges (pension alimentaire, etc.)</label>
                        <ChargesList charges={form.co_borrower_charges_list || []} onChange={v => up('co_borrower_charges_list', v)} />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Investisseurs supplementaires */}
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 mt-4">
                <h3 className="font-semibold text-gray-800 mb-3">Investisseurs supplementaires (SCI, indivision...)</h3>
                <ExtraAssociates list={form.extra_associates || []} onChange={v => up('extra_associates', v)} />
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Votre projet</h2>
              <p className="text-sm text-gray-500 mb-4">Donnez-nous une idee de ce que vous recherchez.</p>
              <div className="grid grid-cols-2 gap-4">
                <FField label="Budget recherche (EUR)" type="number" value={form.target_budget} onChange={v => up('target_budget', parseFloat(v) || null)} />
                <FField label="Apport disponible (EUR)" type="number" value={form.down_payment} onChange={v => up('down_payment', parseFloat(v) || null)} placeholder="0 si sans apport" />
                <FField label="Zone geographique" type="text" value={form.geographic_zone} onChange={v => up('geographic_zone', v)} />
                <FField label="Horizon" type="select" value={form.horizon} onChange={v => up('horizon', v)} opts={[{v:'',l:'Selectionner'},{v:'court',l:'Court terme (< 6 mois)'},{v:'moyen',l:'Moyen terme (6-12 mois)'},{v:'long',l:'Long terme (> 12 mois)'}]} />
                <FField label="Objectif" type="select" value={form.objective} onChange={v => up('objective', v)} opts={[{v:'',l:'Selectionner'},{v:'patrimoine',l:'Constituer un patrimoine'},{v:'revenus',l:'Generer des revenus'},{v:'defiscalisation',l:'Defiscalisation'}]} />
              </div>
              <div className="mt-2">
                <label className="realease-label">Commentaires / Questions</label>
                <textarea value={form.notes} onChange={e => up('notes', e.target.value)} className="realease-input h-28 resize-none" placeholder="Vos questions ou precisions..." />
              </div>
            </div>
          )}

          {error && <p className="text-red-600 text-sm mt-4">{error}</p>}
          {showErrors && !canAdvance && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" data-testid="prequal-errors">
              <p className="font-semibold mb-1">Veuillez completer les champs requis :</p>
              <ul className="list-disc list-inside text-xs">
                {Object.values(currentErrors).map((m, i) => <li key={i}>{m}</li>)}
              </ul>
            </div>
          )}

          <div className="flex justify-between items-center mt-8 pt-6 border-t border-gray-100">
            <button onClick={() => { setShowErrors(false); setStep(s => Math.max(0, s - 1)); }} disabled={step === 0} className="text-gray-500 text-sm font-medium disabled:opacity-30 disabled:cursor-not-allowed">Precedent</button>
            {step < stepTitles.length - 1 ? (
              <button
                onClick={() => {
                  if (!canAdvance) { setShowErrors(true); return; }
                  setShowErrors(false);
                  setStep(s => s + 1);
                }}
                className={`realease-btn-primary ${!canAdvance ? 'opacity-60 cursor-not-allowed' : ''}`}
                data-testid="prequal-next"
              >
                Suivant
              </button>
            ) : (
              <button
                onClick={() => {
                  if (!canAdvance) { setShowErrors(true); return; }
                  handleSubmit();
                }}
                disabled={submitting}
                className={`realease-btn-primary ${(!canAdvance || submitting) ? 'opacity-60 cursor-not-allowed' : ''}`}
                data-testid="prequal-submit"
              >
                {submitting ? 'Envoi...' : 'Envoyer mon dossier'}
              </button>
            )}
          </div>
        </div>

        <p className="text-xs text-gray-500 text-center mt-6">Vos donnees sont confidentielles. Vous serez recontacte sous 48h.</p>
      </div>
    </div>
  );
};

// Step 5 : upload documents
const PreQualUploadStep = ({ prequalId, token, firstName }) => {
  const docTypes = [
    { key: 'Avis_Imposition', label: "Avis d'imposition" },
    { key: 'Bulletins_Salaire', label: "3 derniers bulletins de salaire" },
    { key: 'Identite', label: "Carte d'identite ou passeport" },
    { key: 'Releves_Compte', label: "3 derniers releves de compte" },
    { key: 'Releves_Epargne', label: "Releve d'epargne" },
  ];
  const [uploads, setUploads] = useState({}); // { docType: { uploading, success, filename, error } }

  const handleUpload = async (docType, file) => {
    setUploads(prev => ({ ...prev, [docType]: { uploading: true } }));
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/prequalifications/${prequalId}/upload?token=${token}&doc_type=${docType}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setUploads(prev => ({ ...prev, [docType]: { success: true, filename: r.data.filename } }));
    } catch (err) {
      setUploads(prev => ({ ...prev, [docType]: { error: err.response?.data?.detail || 'Erreur upload' } }));
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-gradient-to-br from-[#008A8D] to-[#006B6E] text-white py-10 px-6">
        <div className="max-w-3xl mx-auto flex items-center gap-4">
          <img src="/realease-square-beige.jpg" alt="RealEase" className="w-16 h-16 rounded-lg shadow-lg object-contain" />
          <div>
            <h1 className="text-2xl font-bold tracking-wider">REALEASE</h1>
            <p className="text-sm opacity-90">Documents complementaires (optionnel)</p>
          </div>
        </div>
      </div>
      <div className="max-w-3xl mx-auto p-6">
        <div className="bg-white rounded-xl shadow-md p-8">
          <div className="text-center mb-8">
            <div className="w-14 h-14 mx-auto mb-3 bg-green-100 rounded-full flex items-center justify-center">
              <svg className="w-7 h-7 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Merci{firstName ? `, ${firstName}` : ''} !</h2>
            <p className="text-gray-600 mt-2">Votre dossier de pre-qualification a bien ete envoye.</p>
            <p className="text-sm text-gray-500 mt-3">Vous pouvez deposer ici quelques documents (totalement optionnel). Cela accelerera votre rendez-vous avec notre equipe.</p>
          </div>
          <div className="space-y-3">
            {docTypes.map(dt => {
              const u = uploads[dt.key] || {};
              return (
                <div key={dt.key} className="border-2 border-dashed border-gray-200 rounded-lg p-4 hover:border-[#008A8D] transition-colors">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <svg className="w-6 h-6 text-gray-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900">{dt.label}</p>
                        {u.success && <p className="text-xs text-green-600 truncate">\u2713 {u.filename} envoye</p>}
                        {u.error && <p className="text-xs text-red-600">{u.error}</p>}
                        {u.uploading && <p className="text-xs text-[#008A8D]">Envoi en cours...</p>}
                      </div>
                    </div>
                    <label className="realease-btn-secondary text-sm cursor-pointer shrink-0">
                      {u.success ? 'Remplacer' : 'Choisir un fichier'}
                      <input type="file" className="hidden" onChange={e => e.target.files[0] && handleUpload(dt.key, e.target.files[0])} data-testid={`upload-${dt.key}`} />
                    </label>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="text-center mt-8">
            <p className="text-sm text-gray-500">Une fois termine, vous pouvez fermer cette page.<br/>Notre equipe vous contactera tres vite.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== PRE-QUALIFICATIONS (ADMIN) ====================
const PreQualAdminPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [viewDetail, setViewDetail] = useState(null); // prequal object or null

  const fetchItems = useCallback(async () => {
    try { const r = await axios.get(`${API}/prequalifications`); setItems(r.data); }
    catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleRemind = async (id) => {
    try {
      await axios.post(`${API}/prequalifications/${id}/remind`);
      alert('Rappel envoye au client');
      fetchItems();
    } catch (err) { alert(err.response?.data?.detail || 'Erreur'); }
  };

  const handleConvert = async (id) => {
    if (!window.confirm('Convertir cette pre-qualification en prospect dans le CRM ?')) return;
    try {
      const r = await axios.post(`${API}/prequalifications/${id}/convert`);
      alert(r.data.already_converted ? 'Deja converti.' : 'Prospect cree dans le CRM.');
      fetchItems();
    } catch (err) { alert(err.response?.data?.detail || 'Erreur'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette pre-qualification ?')) return;
    try { await axios.delete(`${API}/prequalifications/${id}`); fetchItems(); }
    catch (err) { alert(err.response?.data?.detail || 'Erreur'); }
  };

  const copyLink = (token) => {
    const url = `${window.location.origin}/pre-qualification?token=${token}`;
    navigator.clipboard.writeText(url);
    alert('Lien copie dans le presse-papier');
  };

  const universalLink = `${window.location.origin}/pre-qualification`;
  const [copiedUniversal, setCopiedUniversal] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const copyUniversalLink = () => {
    navigator.clipboard.writeText(universalLink);
    setCopiedUniversal(true);
    setTimeout(() => setCopiedUniversal(false), 2500);
  };
  const downloadQR = () => {
    const svg = document.querySelector('[data-testid="universal-qr-svg"]');
    if (!svg) return;
    const xml = new XMLSerializer().serializeToString(svg);
    const blob = new Blob([xml], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'RealEase_QR_Prequalification.svg';
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const downloadQrPng = () => {
    // Convertit le SVG en PNG via un canvas pour un fichier qui s'ouvre partout (Windows photo viewer, etc).
    const svg = document.querySelector('[data-testid="universal-qr-svg"]');
    if (!svg) return;
    const xml = new XMLSerializer().serializeToString(svg);
    const svg64 = btoa(unescape(encodeURIComponent(xml)));
    const imgSrc = 'data:image/svg+xml;base64,' + svg64;
    const img = new Image();
    img.onload = () => {
      const size = 1024;
      const canvas = document.createElement('canvas');
      canvas.width = size; canvas.height = size;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, size, size);
      ctx.drawImage(img, 0, 0, size, size);
      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'RealEase_QR_Prequalification.png';
        a.click();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      }, 'image/png');
    };
    img.src = imgSrc;
  };

  const [qrEmail, setQrEmail] = useState('');
  const [qrEmailNote, setQrEmailNote] = useState('');
  const [qrEmailSending, setQrEmailSending] = useState(false);
  const [qrEmailSent, setQrEmailSent] = useState(false);
  const sendQrByEmail = async () => {
    const target = (qrEmail || '').trim();
    if (!target || !target.includes('@')) { alert('Email destinataire requis'); return; }
    setQrEmailSending(true);
    try {
      await axios.post(`${API}/prequalifications/qr-email`, { email: target, note: qrEmailNote });
      setQrEmailSent(true);
      setTimeout(() => setQrEmailSent(false), 3000);
    } catch (e) {
      alert(e.response?.data?.detail || 'Erreur envoi email');
    } finally { setQrEmailSending(false); }
  };

  const statusColors = { pending: 'bg-yellow-100 text-yellow-800', submitted: 'bg-blue-100 text-blue-800', converted: 'bg-green-100 text-green-800' };
  const statusLabels = { pending: 'En attente', submitted: 'Rempli', converted: 'Converti' };

  return (
    <div className="p-4 lg:p-8" data-testid="prequal-admin-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Pre-qualifications</h1>
          <p className="text-gray-600 mt-1">Envoyez un lien de pre-qualif au client avant le RDV</p>
        </div>
        <button onClick={() => setShowInvite(true)} className="realease-btn-primary flex items-center gap-2" data-testid="invite-prequal-btn">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
          Inviter un client
        </button>
      </div>

      {/* Lien universel - toujours visible */}
      <div className="realease-card p-4 mb-6 bg-gradient-to-r from-[#FDF8F0] to-[#F5EDD8] border-[#E8DCC4]" data-testid="universal-link-card">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex-shrink-0">
            <div className="w-10 h-10 rounded-full bg-[#A57F4F]/20 flex items-center justify-center">
              <svg className="w-5 h-5 text-[#A57F4F]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 015.656 0l1.414 1.414a4 4 0 010 5.656l-3.535 3.536a4 4 0 01-5.657 0l-1.414-1.415m-3.536-3.535a4 4 0 010-5.656L10.292 6.636a4 4 0 015.657 0l1.414 1.414" /></svg>
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-[#8C6A3F]">Lien universel de pre-qualification</p>
            <p className="text-xs text-gray-600 mt-0.5">A copier-coller dans vos emails, SMS, sites web ou QR codes. Tout client peut remplir sa pre-qualif via ce lien, deposer ses pieces, et son dossier sera cree automatiquement dans la liste ci-dessous.</p>
            <p className="mt-2 px-3 py-1.5 bg-white border border-gray-200 rounded text-xs font-mono text-gray-700 truncate" data-testid="universal-link-url">{universalLink}</p>
          </div>
          <button
            onClick={copyUniversalLink}
            className={`px-4 py-2 rounded-lg font-semibold text-sm flex items-center gap-2 transition-colors ${copiedUniversal ? 'bg-emerald-600 text-white' : 'bg-[#A57F4F] hover:bg-[#8C6A3F] text-white'}`}
            data-testid="copy-universal-link">
            {copiedUniversal ? (
              <><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg> Copie !</>
            ) : (
              <><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" /></svg> Copier</>
            )}
          </button>
          <button
            onClick={() => setShowQR(s => !s)}
            className={`px-3 py-2 border rounded-lg text-sm font-medium flex items-center gap-1.5 ${showQR ? 'border-[#A57F4F] bg-[#A57F4F]/10 text-[#8C6A3F]' : 'border-[#A57F4F]/30 text-[#A57F4F] hover:bg-[#A57F4F]/10'}`}
            data-testid="toggle-universal-qr">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" /></svg>
            QR code
          </button>
          <a
            href={universalLink} target="_blank" rel="noreferrer"
            className="px-3 py-2 border border-[#A57F4F]/30 text-[#A57F4F] hover:bg-[#A57F4F]/10 rounded-lg text-sm font-medium"
            data-testid="open-universal-link">
            Ouvrir
          </a>
        </div>
        {showQR && (
          <div className="mt-4 pt-4 border-t border-[#E8DCC4] flex flex-col sm:flex-row items-center gap-5" data-testid="universal-qr-panel">
            <div className="p-3 bg-white rounded-lg border border-[#E8DCC4] shadow-sm">
              <QRCodeSVG
                value={universalLink}
                size={180}
                level="M"
                fgColor="#005558"
                bgColor="#FFFFFF"
                imageSettings={{ src: '/realease-square-beige.jpg', height: 36, width: 36, excavate: true }}
                data-testid="universal-qr-svg"
              />
            </div>
            <div className="flex-1 text-sm text-gray-700 space-y-3">
              <p className="font-semibold text-[#8C6A3F]">QR code de pre-qualification</p>
              <p className="text-xs text-gray-600">A imprimer sur vos cartes de visite, flyers, affiches en vitrine, ou a inserer dans vos signatures email / plaquettes commerciales. Un prospect scanne -&gt; arrive directement sur le formulaire.</p>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={downloadQrPng}
                  className="px-3 py-1.5 bg-[#A57F4F] hover:bg-[#8C6A3F] text-white text-xs font-semibold rounded inline-flex items-center gap-1.5"
                  data-testid="download-universal-qr-png">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                  Telecharger PNG (1024px)
                </button>
                <button
                  onClick={downloadQR}
                  className="px-3 py-1.5 bg-white border border-[#A57F4F] text-[#A57F4F] hover:bg-[#A57F4F]/10 text-xs font-semibold rounded inline-flex items-center gap-1.5"
                  data-testid="download-universal-qr">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                  Telecharger SVG (vectoriel)
                </button>
              </div>
              <p className="text-[10px] text-gray-400">PNG = ouvre partout (Windows, Mac, Telephone). SVG = vectoriel, ideal pour impression grande taille.</p>

              {/* Envoyer par email */}
              <div className="pt-3 border-t border-[#E8DCC4]">
                <p className="text-xs font-semibold text-gray-700 mb-2">Envoyer par email (avec PNG + SVG en pieces jointes)</p>
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    type="email"
                    value={qrEmail}
                    onChange={e => setQrEmail(e.target.value)}
                    placeholder="adresse@email.com"
                    className="flex-1 px-3 py-1.5 border border-gray-200 rounded text-xs focus:border-[#A57F4F] focus:outline-none"
                    data-testid="qr-email-input"
                  />
                  <button
                    onClick={sendQrByEmail}
                    disabled={qrEmailSending || !qrEmail.includes('@')}
                    className={`px-3 py-1.5 text-xs font-semibold rounded inline-flex items-center gap-1.5 ${qrEmailSent ? 'bg-emerald-600 text-white' : 'bg-[#008A8D] hover:bg-[#006B6E] text-white disabled:opacity-50'}`}
                    data-testid="qr-email-send">
                    {qrEmailSent ? (
                      <><svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg> Envoye !</>
                    ) : qrEmailSending ? (
                      <>Envoi...</>
                    ) : (
                      <><svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg> Envoyer par email</>
                    )}
                  </button>
                </div>
                <input
                  type="text"
                  value={qrEmailNote}
                  onChange={e => setQrEmailNote(e.target.value)}
                  placeholder="Note optionnelle (ex: Voici le QR a imprimer sur les cartes de visite)"
                  className="mt-2 w-full px-3 py-1.5 border border-gray-200 rounded text-xs focus:border-[#A57F4F] focus:outline-none"
                  data-testid="qr-email-note"
                />
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="realease-card overflow-hidden">
        {loading ? (
          <div className="p-8 text-center"><div className="w-8 h-8 border-4 border-t-[#008A8D] border-gray-200 rounded-full animate-spin mx-auto"></div></div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-gray-500">Aucune pre-qualification. Cliquez sur "Inviter un client" pour commencer.</div>
        ) : (
          <table className="realease-table">
            <thead><tr><th>Nom</th><th>Email</th><th>Telephone</th><th>Branche</th><th>Statut</th><th>Documents</th><th>Date</th><th>Actions</th></tr></thead>
            <tbody>
              {items.map(p => (
                <tr key={p.prequal_id} data-testid={`prequal-row-${p.prequal_id}`}>
                  <td className="font-medium">{p.first_name} {p.last_name}</td>
                  <td>{p.email}</td>
                  <td>{p.phone || '-'}</td>
                  <td>
                    <span className={`inline-block text-[10px] px-1.5 py-0.5 rounded font-semibold ${p.branch === 'nord' ? 'bg-blue-100 text-blue-700' : 'bg-emerald-100 text-emerald-700'}`}>
                      {p.branch === 'nord' ? 'Nord' : 'Bretagne'}
                    </span>
                  </td>
                  <td><span className={`status-badge ${statusColors[p.status] || ''}`}>{statusLabels[p.status]}</span></td>
                  <td className="text-sm text-gray-600">{(p.documents || []).length}</td>
                  <td className="text-sm text-gray-500">{new Date(p.created_at).toLocaleDateString('fr-FR')}</td>
                  <td className="flex gap-2 items-center flex-wrap">
                    {(p.status === 'submitted' || p.status === 'converted') && (
                      <button onClick={() => setViewDetail(p)} className="text-[#008A8D] hover:underline text-sm font-medium" data-testid={`view-detail-${p.prequal_id}`}>Voir</button>
                    )}
                    {p.status === 'pending' && p.token && (
                      <>
                        <button onClick={() => copyLink(p.token)} className="text-[#008A8D] hover:underline text-sm font-medium" data-testid={`copy-link-${p.prequal_id}`}>Copier lien</button>
                        <button onClick={() => handleRemind(p.prequal_id)} className="text-orange-600 hover:underline text-sm font-medium" data-testid={`remind-${p.prequal_id}`}>Relancer</button>
                      </>
                    )}
                    {p.status === 'submitted' && (
                      <button onClick={() => handleConvert(p.prequal_id)} className="text-green-600 hover:underline text-sm font-medium" data-testid={`convert-${p.prequal_id}`}>Convertir en prospect</button>
                    )}
                    {p.status === 'converted' && p.lead_id && (
                      <a href={`/qualification/${p.lead_id}`} className="text-purple-600 hover:underline text-sm font-medium">Voir le prospect</a>
                    )}
                    {p.drive_folder?.root_url && (
                      <a href={p.drive_folder.root_url} target="_blank" rel="noreferrer" className="text-gray-600 hover:underline text-sm">Drive</a>
                    )}
                    <button onClick={() => handleDelete(p.prequal_id)} className="text-red-500 hover:underline text-sm">Suppr.</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {showInvite && <PreQualInviteModal onClose={() => setShowInvite(false)} onSuccess={() => { setShowInvite(false); fetchItems(); }} />}
      {viewDetail && (
        <PreQualDetailModal
          prequal={viewDetail}
          onClose={() => setViewDetail(null)}
          onConvert={async () => {
            if (!window.confirm('Convertir cette pre-qualification en prospect dans le CRM ?')) return;
            try {
              const r = await axios.post(`${API}/prequalifications/${viewDetail.prequal_id}/convert`);
              alert(r.data.already_converted ? 'Deja converti.' : 'Prospect cree dans le CRM.');
              setViewDetail(null);
              fetchItems();
            } catch (err) { alert(err.response?.data?.detail || 'Erreur'); }
          }}
        />
      )}
    </div>
  );
};

// ==================== PRE-QUAL DETAIL MODAL (Iter 113f) ====================
const PrequalSection = ({ title, children }) => (
  <div className="mb-5">
    <h4 className="text-sm font-bold text-[#005558] uppercase tracking-wide mb-2 border-b border-gray-200 pb-1">{title}</h4>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">{children}</div>
  </div>
);

const PrequalRow = ({ label, value }) => {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div className="flex justify-between gap-3 border-b border-dashed border-gray-100 py-1">
      <span className="text-gray-500">{label}</span>
      <span className="font-medium text-gray-900 text-right">{value}</span>
    </div>
  );
};

const PreQualDetailModal = ({ prequal, onClose, onConvert }) => {
  const { user } = useAuth();
  const [currentBranch, setCurrentBranch] = useState(prequal.branch || 'bretagne');
  const [changingBranch, setChangingBranch] = useState(false);
  const d = prequal.data || {};
  const isConverted = prequal.status === 'converted';
  const fmtMoney = (v) => (v == null || v === '' ? null : `${Number(v).toLocaleString('fr-FR')} EUR`);
  const canReassign = user && ['ceo', 'admin'].includes(user.role);

  const FAMILY = {celibataire: 'Celibataire', marie: 'Marie(e)', pacse: 'Pacse(e)', divorce: 'Divorce(e)'};
  const PRO = {cdi: 'CDI', fonctionnaire: 'Fonctionnaire', cdd: 'CDD', interim: 'Interim / Mission', independant: 'Independant / Liberal', gerant: 'Gerant / Chef d\'entreprise', retraite: 'Retraite(e)', precaire: 'Precaire / Sans emploi', salarie: 'Salarie(e)'};
  const HORIZON = {court: 'Court terme (<6 mois)', moyen: 'Moyen terme (6-12 mois)', long: 'Long terme (>12 mois)'};
  const OBJECTIVE = {patrimoine: 'Constituer un patrimoine', revenus: 'Generer des revenus', defiscalisation: 'Defiscalisation'};

  const rentals = d.rentals_list || [];
  const credits = d.credits || [];
  const charges = d.charges_list || [];
  const coRentals = d.co_borrower_rentals_list || [];
  const coCredits = d.co_borrower_credits_list || [];
  const coCharges = d.co_borrower_charges_list || [];
  const extras = d.extra_associates || [];
  const rentalsTotal = rentals.reduce((s, r) => s + (parseFloat(r.rent) || 0), 0);
  const creditsTotal = credits.reduce((s, r) => s + (parseFloat(r.monthly) || 0), 0);
  const chargesTotal = charges.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
  const coRentalsTotal = coRentals.reduce((s, r) => s + (parseFloat(r.rent) || 0), 0);
  const coCreditsTotal = coCredits.reduce((s, r) => s + (parseFloat(r.monthly) || 0), 0);
  const coChargesTotal = coCharges.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);

  const reassignBranch = async (newBranch) => {
    if (newBranch === currentBranch) return;
    if (!window.confirm(`Re-assigner cette pre-qualification a la branche ${newBranch === 'nord' ? 'Nord' : 'Bretagne'} ?`)) return;
    setChangingBranch(true);
    try {
      await axios.put(`${API}/prequalifications/${prequal.prequal_id}/branch`, { branch: newBranch });
      setCurrentBranch(newBranch);
    } catch (e) {
      alert(e.response?.data?.detail || 'Erreur re-assignation');
    } finally { setChangingBranch(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="prequal-detail-modal">
      <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-gray-200 sticky top-0 bg-white z-10 flex items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Pre-qualification : {prequal.first_name} {prequal.last_name}</h2>
            <p className="text-sm text-gray-500 mt-1 flex items-center gap-2 flex-wrap">
              <span>Soumise le {prequal.submitted_at ? new Date(prequal.submitted_at).toLocaleDateString('fr-FR') : '-'} ·</span>
              {canReassign ? (
                <span className="inline-flex items-center gap-1" data-testid="prequal-branch-reassign">
                  <span className="text-xs text-gray-500">Branche :</span>
                  <select
                    value={currentBranch}
                    onChange={(e) => reassignBranch(e.target.value)}
                    disabled={changingBranch}
                    className={`text-[11px] font-semibold rounded px-1.5 py-0.5 border border-transparent cursor-pointer ${currentBranch === 'nord' ? 'bg-blue-100 text-blue-700' : 'bg-emerald-100 text-emerald-700'}`}
                    data-testid="prequal-branch-select"
                  >
                    <option value="bretagne">Bretagne</option>
                    <option value="nord">Nord</option>
                  </select>
                </span>
              ) : (
                <span className={`inline-block text-[10px] px-1.5 py-0.5 rounded font-semibold ${currentBranch === 'nord' ? 'bg-blue-100 text-blue-700' : 'bg-emerald-100 text-emerald-700'}`}>
                  {currentBranch === 'nord' ? 'Nord' : 'Bretagne'}
                </span>
              )}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-2xl leading-none" data-testid="prequal-detail-close">×</button>
        </div>

        <div className="p-6">
          <PrequalSection title="Contact">
            <PrequalRow label="Email" value={prequal.email} />
            <PrequalRow label="Telephone" value={prequal.phone} />
            <PrequalRow label="Adresse" value={prequal.address} />
            <PrequalRow label="Reference" value={prequal.prequal_id} />
          </PrequalSection>

          <PrequalSection title="Situation personnelle">
            <PrequalRow label="Age" value={d.age} />
            <PrequalRow label="Situation familiale" value={FAMILY[d.family_situation] || d.family_situation} />
            <PrequalRow label="Enfants a charge" value={d.children_count ?? 0} />
            <PrequalRow label="Situation professionnelle" value={PRO[d.professional_situation] || d.professional_situation} />
          </PrequalSection>

          <PrequalSection title="Investisseur 1 (client)">
            <PrequalRow label="Revenu net fiscal mensuel" value={fmtMoney(d.monthly_net_income)} />
            <PrequalRow label="Revenu fiscal de reference (annuel)" value={fmtMoney(d.annual_reference_income)} />
            <PrequalRow label="Stabilite" value={PRO[d.income_stability] || d.income_stability} />
            <PrequalRow label={`Loyers percus (${rentals.length})`} value={rentalsTotal ? fmtMoney(rentalsTotal) : (rentals.length ? '0 EUR' : null)} />
            <PrequalRow label={`Credits en cours (${credits.length})`} value={creditsTotal ? fmtMoney(creditsTotal) + '/mois' : (credits.length ? '0 EUR' : null)} />
            <PrequalRow label={`Charges (${charges.length})`} value={chargesTotal ? fmtMoney(chargesTotal) + '/mois' : (charges.length ? '0 EUR' : null)} />
          </PrequalSection>

          {rentals.length > 0 && (
            <div className="mb-5">
              <p className="text-xs font-semibold text-gray-600 mb-1">Detail loyers percus (I1)</p>
              <table className="w-full text-xs border border-gray-200 rounded"><thead className="bg-gray-50"><tr><th className="text-left px-2 py-1">Bien</th><th className="text-left px-2 py-1">Adresse</th><th className="text-right px-2 py-1">Loyer/mois</th></tr></thead><tbody>
                {rentals.map((r, i) => (<tr key={i} className="border-t border-gray-100"><td className="px-2 py-1">{r.label || '-'}</td><td className="px-2 py-1 text-gray-500">{r.address || '-'}</td><td className="px-2 py-1 text-right font-medium">{fmtMoney(r.rent) || '-'}</td></tr>))}
              </tbody></table>
            </div>
          )}
          {credits.length > 0 && (
            <div className="mb-5">
              <p className="text-xs font-semibold text-gray-600 mb-1">Detail credits en cours (I1)</p>
              <table className="w-full text-xs border border-gray-200 rounded"><thead className="bg-gray-50"><tr><th className="text-left px-2 py-1">Type</th><th className="text-right px-2 py-1">Mensualite</th><th className="text-right px-2 py-1">Fin</th></tr></thead><tbody>
                {credits.map((r, i) => (<tr key={i} className="border-t border-gray-100"><td className="px-2 py-1">{r.type || r.label || '-'}</td><td className="px-2 py-1 text-right font-medium">{fmtMoney(r.monthly) || '-'}</td><td className="px-2 py-1 text-right text-gray-500">{r.end_date || '-'}</td></tr>))}
              </tbody></table>
            </div>
          )}
          {charges.length > 0 && (
            <div className="mb-5">
              <p className="text-xs font-semibold text-gray-600 mb-1">Detail charges (I1)</p>
              <table className="w-full text-xs border border-gray-200 rounded"><thead className="bg-gray-50"><tr><th className="text-left px-2 py-1">Type</th><th className="text-right px-2 py-1">Montant/mois</th></tr></thead><tbody>
                {charges.map((r, i) => (<tr key={i} className="border-t border-gray-100"><td className="px-2 py-1">{r.type || r.label || '-'}</td><td className="px-2 py-1 text-right font-medium">{fmtMoney(r.amount) || '-'}</td></tr>))}
              </tbody></table>
            </div>
          )}

          {d.has_co_borrower && (
            <>
              <PrequalSection title={`Investisseur 2 ${d.co_borrower_name ? `- ${d.co_borrower_name}` : ''}`}>
                <PrequalRow label="Nom" value={d.co_borrower_name} />
                <PrequalRow label="Lien" value={d.co_borrower_relation} />
                <PrequalRow label="Revenu net fiscal mensuel" value={fmtMoney(d.co_borrower_income)} />
                <PrequalRow label="Revenu fiscal de reference (annuel)" value={fmtMoney(d.co_borrower_annual_reference_income)} />
                <PrequalRow label="Stabilite" value={PRO[d.co_borrower_stability] || d.co_borrower_stability} />
                <PrequalRow label={`Loyers percus (${coRentals.length})`} value={coRentalsTotal ? fmtMoney(coRentalsTotal) : (coRentals.length ? '0 EUR' : null)} />
                <PrequalRow label={`Credits en cours (${coCredits.length})`} value={coCreditsTotal ? fmtMoney(coCreditsTotal) + '/mois' : (coCredits.length ? '0 EUR' : null)} />
                <PrequalRow label={`Charges (${coCharges.length})`} value={coChargesTotal ? fmtMoney(coChargesTotal) + '/mois' : (coCharges.length ? '0 EUR' : null)} />
              </PrequalSection>
              {coRentals.length > 0 && (
                <div className="mb-5">
                  <p className="text-xs font-semibold text-gray-600 mb-1">Detail loyers percus (I2)</p>
                  <table className="w-full text-xs border border-gray-200 rounded"><thead className="bg-gray-50"><tr><th className="text-left px-2 py-1">Bien</th><th className="text-left px-2 py-1">Adresse</th><th className="text-right px-2 py-1">Loyer/mois</th></tr></thead><tbody>
                    {coRentals.map((r, i) => (<tr key={i} className="border-t border-gray-100"><td className="px-2 py-1">{r.label || '-'}</td><td className="px-2 py-1 text-gray-500">{r.address || '-'}</td><td className="px-2 py-1 text-right font-medium">{fmtMoney(r.rent) || '-'}</td></tr>))}
                  </tbody></table>
                </div>
              )}
              {coCredits.length > 0 && (
                <div className="mb-5">
                  <p className="text-xs font-semibold text-gray-600 mb-1">Detail credits en cours (I2)</p>
                  <table className="w-full text-xs border border-gray-200 rounded"><thead className="bg-gray-50"><tr><th className="text-left px-2 py-1">Type</th><th className="text-right px-2 py-1">Mensualite</th><th className="text-right px-2 py-1">Fin</th></tr></thead><tbody>
                    {coCredits.map((r, i) => (<tr key={i} className="border-t border-gray-100"><td className="px-2 py-1">{r.type || r.label || '-'}</td><td className="px-2 py-1 text-right font-medium">{fmtMoney(r.monthly) || '-'}</td><td className="px-2 py-1 text-right text-gray-500">{r.end_date || '-'}</td></tr>))}
                  </tbody></table>
                </div>
              )}
              {coCharges.length > 0 && (
                <div className="mb-5">
                  <p className="text-xs font-semibold text-gray-600 mb-1">Detail charges (I2)</p>
                  <table className="w-full text-xs border border-gray-200 rounded"><thead className="bg-gray-50"><tr><th className="text-left px-2 py-1">Type</th><th className="text-right px-2 py-1">Montant/mois</th></tr></thead><tbody>
                    {coCharges.map((r, i) => (<tr key={i} className="border-t border-gray-100"><td className="px-2 py-1">{r.type || r.label || '-'}</td><td className="px-2 py-1 text-right font-medium">{fmtMoney(r.amount) || '-'}</td></tr>))}
                  </tbody></table>
                </div>
              )}
            </>
          )}

          {extras.length > 0 && (
            <div className="mb-5">
              <h4 className="text-sm font-bold text-[#005558] uppercase tracking-wide mb-2 border-b border-gray-200 pb-1">Autres investisseurs ({extras.length})</h4>
              <table className="w-full text-xs border border-gray-200 rounded"><thead className="bg-gray-50"><tr><th className="text-left px-2 py-1">Nom</th><th className="text-left px-2 py-1">Lien</th><th className="text-right px-2 py-1">Revenu mensuel</th><th className="text-right px-2 py-1">Apport</th></tr></thead><tbody>
                {extras.map((a, i) => (<tr key={i} className="border-t border-gray-100"><td className="px-2 py-1 font-medium">{a.name || '-'}</td><td className="px-2 py-1 text-gray-500">{a.relation || '-'}</td><td className="px-2 py-1 text-right">{fmtMoney(a.monthly_income) || '-'}</td><td className="px-2 py-1 text-right">{fmtMoney(a.down_payment) || '-'}</td></tr>))}
              </tbody></table>
            </div>
          )}

          <PrequalSection title="Projet immobilier">
            <PrequalRow label="Budget recherche" value={fmtMoney(d.target_budget)} />
            <PrequalRow label="Apport disponible" value={fmtMoney(d.down_payment)} />
            <PrequalRow label="Zone geographique" value={d.geographic_zone} />
            <PrequalRow label="Horizon" value={HORIZON[d.horizon] || d.horizon} />
            <PrequalRow label="Objectif" value={OBJECTIVE[d.objective] || d.objective} />
          </PrequalSection>

          {d.notes && (
            <PrequalSection title="Notes du client">
              <div className="col-span-2 bg-amber-50 border border-amber-200 rounded p-3 text-sm text-gray-800 whitespace-pre-wrap">{d.notes}</div>
            </PrequalSection>
          )}

          {(prequal.documents || []).length > 0 && (
            <PrequalSection title="Documents fournis">
              <ul className="col-span-2 list-disc list-inside text-sm text-gray-700">
                {(prequal.documents || []).map((doc, i) => (
                  <li key={i}>
                    {doc.name || doc.file_name || doc.filename || 'document'} <span className="text-gray-400 text-xs">({doc.doc_type || doc.type || 'autre'})</span>
                    {doc.webViewLink && <a href={doc.webViewLink} target="_blank" rel="noreferrer" className="ml-2 text-[#008A8D] hover:underline text-xs">Ouvrir</a>}
                  </li>
                ))}
              </ul>
            </PrequalSection>
          )}

          {prequal.drive_folder?.root_url && (
            <div className="mt-4">
              <a href={prequal.drive_folder.root_url} target="_blank" rel="noreferrer" className="text-sm text-[#A57F4F] hover:underline inline-flex items-center gap-1">
                Ouvrir le dossier Drive de la pre-qualification
              </a>
            </div>
          )}
        </div>

        <div className="p-6 border-t border-gray-200 sticky bottom-0 bg-white flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm font-semibold text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50" data-testid="prequal-detail-cancel">Fermer</button>
          {!isConverted && (
            <button onClick={onConvert} className="realease-btn-primary" data-testid="prequal-detail-convert">
              Convertir en prospect
            </button>
          )}
          {isConverted && prequal.lead_id && (
            <a href={`/qualification/${prequal.lead_id}`} className="realease-btn-primary inline-flex items-center">Voir le prospect &rarr;</a>
          )}
        </div>
      </div>
    </div>
  );
};

const PreQualInviteModal = ({ onClose, onSuccess }) => {
  const [form, setForm] = useState({ email: '', first_name: '', last_name: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [createdLink, setCreatedLink] = useState(null);

  const submit = async () => {
    if (!form.email) { setError('Email obligatoire'); return; }
    setSubmitting(true); setError(null);
    try {
      const r = await axios.post(`${API}/prequalifications/invite`, form);
      const link = `${window.location.origin}/pre-qualification?token=${r.data.token}`;
      setCreatedLink(link);
    } catch (err) { setError(err.response?.data?.detail || 'Erreur'); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
        {!createdLink ? (
          <>
            <h2 className="text-xl font-semibold mb-4">Inviter un client</h2>
            <p className="text-sm text-gray-500 mb-4">Envoyez un lien de pre-qualification par email au client. Il pourra remplir ses infos et deposer ses documents.</p>
            <div className="space-y-3">
              <div><label className="realease-label">Email *</label><input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="realease-input" data-testid="invite-email" /></div>
              <div><label className="realease-label">Prenom</label><input type="text" value={form.first_name} onChange={e => setForm({...form, first_name: e.target.value})} className="realease-input" /></div>
              <div><label className="realease-label">Nom</label><input type="text" value={form.last_name} onChange={e => setForm({...form, last_name: e.target.value})} className="realease-input" /></div>
            </div>
            {error && <p className="text-red-600 text-sm mt-2">{error}</p>}
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={onClose} className="text-gray-500 text-sm px-4 py-2">Annuler</button>
              <button onClick={submit} disabled={submitting} className="realease-btn-primary" data-testid="confirm-invite">{submitting ? 'Envoi...' : 'Envoyer le lien'}</button>
            </div>
          </>
        ) : (
          <>
            <h2 className="text-xl font-semibold mb-3">Invitation envoyee</h2>
            <p className="text-sm text-gray-600 mb-4">L'email a ete envoye a <strong>{form.email}</strong>. Vous pouvez aussi copier le lien ci-dessous :</p>
            <div className="p-3 bg-gray-50 rounded-lg break-all text-xs text-gray-700 mb-4">{createdLink}</div>
            <div className="flex justify-end gap-2">
              <button onClick={() => { navigator.clipboard.writeText(createdLink); alert('Lien copie'); }} className="realease-btn-secondary text-sm">Copier</button>
              <button onClick={onSuccess} className="realease-btn-primary text-sm">OK</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ==================== CALENDAR SETTINGS (TEAM) ====================
const CalendarSettingsPage = () => {
  const [status, setStatus] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [bookings, setBookings] = useState([]);
  const params = new URLSearchParams(window.location.search);
  const successFlag = params.get('success');
  const errorFlag = params.get('error');

  const fetchStatus = async () => {
    try {
      const r = await axios.get(`${API}/auth/google-calendar/status`);
      setStatus(r.data);
      setSettings(r.data.settings);
    } catch {} finally { setLoading(false); }
  };

  const fetchBookings = async () => {
    // Optional: list bookings -- for now empty since not exposed
  };

  useEffect(() => { fetchStatus(); fetchBookings(); }, []);

  const connect = async () => {
    try {
      const r = await axios.get(`${API}/auth/google-calendar/start`);
      window.location.href = r.data.auth_url;
    } catch (err) { alert(err.response?.data?.detail || 'Erreur'); }
  };

  const disconnect = async () => {
    if (!window.confirm('Deconnecter votre agenda Google ?')) return;
    try { await axios.delete(`${API}/auth/google-calendar/disconnect`); fetchStatus(); }
    catch (err) { alert(err.response?.data?.detail || 'Erreur'); }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/calendar/settings`, settings);
      alert('Parametres enregistres');
    } catch (err) { alert(err.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  const { user } = useAuth();
  const bookingLink = user ? `${window.location.origin}/booking/${user.user_id}` : '';

  if (loading) return <div className="p-8"><div className="w-8 h-8 border-4 border-t-[#008A8D] border-gray-200 rounded-full animate-spin"></div></div>;

  return (
    <div className="p-4 lg:p-8 max-w-4xl" data-testid="calendar-settings-page">
      <h1 className="text-3xl font-bold text-gray-900 mb-2">Mon agenda</h1>
      <p className="text-gray-600 mb-6">Connectez votre Google Agenda pour permettre aux clients de prendre RDV directement.</p>

      {/* Banniere info publication Google Cloud Console */}
      <details className="realease-card mb-6 bg-amber-50 border border-amber-200 group" data-testid="gcal-publish-info">
        <summary className="p-4 cursor-pointer flex items-center gap-3 list-none">
          <svg className="w-5 h-5 text-amber-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          <div className="flex-1">
            <p className="font-semibold text-amber-900 text-sm">Vous voyez "Application non validee" ou "En cours de test" ?</p>
            <p className="text-xs text-amber-700">C'est normal tant que l'application Google n'est pas publiee. Cliquez ici pour la marche a suivre.</p>
          </div>
          <svg className="w-4 h-4 text-amber-600 transition-transform group-open:rotate-90" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
        </summary>
        <div className="px-5 pb-5 pt-1 text-sm text-amber-900 space-y-2">
          <p>Aucune modification de code n'est requise. Suivez ces etapes une seule fois dans la <a href="https://console.cloud.google.com/" target="_blank" rel="noreferrer" className="underline font-semibold">Google Cloud Console</a> du compte qui heberge l'app OAuth :</p>
          <ol className="list-decimal list-inside space-y-1 ml-1">
            <li>Selectionnez le projet RealEase (en haut de la page).</li>
            <li>Menu lateral &rarr; <b>APIs &amp; Services</b> &rarr; <b>OAuth consent screen</b> (ecran de consentement).</li>
            <li>Verifiez que l'app est en <b>External</b> et que les scopes <code>calendar.readonly</code> + <code>calendar.events</code> sont declares.</li>
            <li>Cliquez sur <b>Publier l'application</b> (Publishing status : Testing &rarr; <b>In production</b>).</li>
            <li>Si Google demande une verification (logo, lien privacy), elle est obligatoire seulement si plus de 100 utilisateurs externes &mdash; en interne, vous pouvez confirmer immediatement.</li>
            <li>Revenez ici, deconnectez-reconnectez l'agenda pour valider.</li>
          </ol>
          <p className="text-xs text-amber-700 italic mt-2">Astuce : tant que l'app reste en "Testing", seuls les emails listes dans <b>Test users</b> peuvent se connecter. La publication retire cette limite.</p>
        </div>
      </details>

      {successFlag && <div className="realease-card p-4 mb-6 bg-green-50 border-green-200 text-green-700">Agenda connecte avec succes !</div>}
      {errorFlag && <div className="realease-card p-4 mb-6 bg-red-50 border-red-200 text-red-700">Erreur de connexion : {errorFlag}</div>}

      <div className="realease-card p-6 mb-6">
        <h2 className="font-semibold text-lg mb-4">Connexion a Google Agenda</h2>
        {status?.connected ? (
          <div>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              <span className="text-sm">Connecte avec <b>{status.email}</b></span>
            </div>
            <p className="text-xs text-gray-500 mb-4">Connecte le {status.connected_at && new Date(status.connected_at).toLocaleDateString('fr-FR')}</p>
            <button onClick={disconnect} className="text-red-600 text-sm hover:underline" data-testid="disconnect-gcal">Deconnecter</button>
          </div>
        ) : (
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-3 h-3 bg-gray-300 rounded-full"></div>
              <span className="text-sm text-gray-600">Aucun agenda connecte</span>
            </div>
            <button onClick={connect} className="realease-btn-primary" data-testid="connect-gcal">Connecter mon Google Agenda</button>
          </div>
        )}
      </div>

      {status?.connected && (
        <>
          <div className="realease-card p-6 mb-6">
            <h2 className="font-semibold text-lg mb-1">Lien public de prise de rendez-vous</h2>
            <p className="text-sm text-gray-500 mb-3">Partagez ce lien avec vos clients pour qu'ils choisissent un creneau.</p>
            <div className="flex items-center gap-2">
              <input readOnly value={bookingLink} className="realease-input flex-1 text-sm" />
              <button onClick={() => { navigator.clipboard.writeText(bookingLink); alert('Lien copie'); }} className="realease-btn-secondary text-sm" data-testid="copy-booking-link">Copier</button>
              <a href={bookingLink} target="_blank" rel="noreferrer" className="realease-btn-secondary text-sm">Voir</a>
            </div>
          </div>

          <div className="realease-card p-6">
            <h2 className="font-semibold text-lg mb-4">Reglages des creneaux</h2>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="realease-label">Duree d'un creneau (min)</label>
                <select value={settings.slot_duration_minutes} onChange={e => setSettings({...settings, slot_duration_minutes: parseInt(e.target.value)})} className="realease-input">
                  <option value={15}>15</option><option value={30}>30</option><option value={45}>45</option><option value={60}>60</option>
                </select>
              </div>
              <div><label className="realease-label">Tampon entre RDV (min)</label>
                <select value={settings.buffer_minutes} onChange={e => setSettings({...settings, buffer_minutes: parseInt(e.target.value)})} className="realease-input">
                  <option value={0}>0</option><option value={15}>15</option><option value={30}>30</option>
                </select>
              </div>
              <div><label className="realease-label">Heure de debut</label>
                <input type="number" min="0" max="23" value={settings.business_hours_start} onChange={e => setSettings({...settings, business_hours_start: parseInt(e.target.value)})} className="realease-input" />
              </div>
              <div><label className="realease-label">Heure de fin</label>
                <input type="number" min="0" max="23" value={settings.business_hours_end} onChange={e => setSettings({...settings, business_hours_end: parseInt(e.target.value)})} className="realease-input" />
              </div>
              <div><label className="realease-label">Visible jusqu'a (jours)</label>
                <input type="number" min="1" max="60" value={settings.days_ahead} onChange={e => setSettings({...settings, days_ahead: parseInt(e.target.value)})} className="realease-input" />
              </div>
              <div className="flex items-center gap-2 pt-6">
                <input type="checkbox" checked={settings.weekdays_only} onChange={e => setSettings({...settings, weekdays_only: e.target.checked})} className="w-4 h-4 accent-[#008A8D]" id="wd" />
                <label htmlFor="wd" className="text-sm text-gray-700">Jours ouvres uniquement</label>
              </div>
            </div>
            <button onClick={saveSettings} disabled={saving} className="realease-btn-primary mt-5" data-testid="save-cal-settings">{saving ? 'Enregistrement...' : 'Enregistrer'}</button>
          </div>
        </>
      )}
    </div>
  );
};

// ==================== PUBLIC BOOKING PAGE ====================
const PublicBookingPage = () => {
  const path = window.location.pathname;
  const userId = path.split('/booking/')[1] || '';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [form, setForm] = useState({ client_name: '', client_email: '', client_phone: '', notes: '' });
  const [confirming, setConfirming] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  useEffect(() => {
    if (!userId) return;
    axios.get(`${API}/calendar/free-slots/${userId}`)
      .then(r => { setData(r.data); setLoading(false); })
      .catch(err => { setError(err.response?.data?.detail || 'Erreur'); setLoading(false); });
  }, [userId]);

  const submit = async () => {
    if (!form.client_name || !form.client_email) { alert('Nom et email obligatoires'); return; }
    setConfirming(true);
    try {
      const r = await axios.post(`${API}/calendar/book`, {
        user_id: userId,
        slot_start: selectedSlot.start,
        ...form
      });
      setConfirmation(r.data);
    } catch (err) { alert(err.response?.data?.detail || 'Erreur'); }
    finally { setConfirming(false); }
  };

  // Group slots by day
  const slotsByDay = useMemo(() => {
    if (!data?.slots) return {};
    const grouped = {};
    data.slots.forEach(s => {
      const dayKey = s.label_local.split(' - ')[0];
      if (!grouped[dayKey]) grouped[dayKey] = [];
      grouped[dayKey].push(s);
    });
    return grouped;
  }, [data]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-4 border-t-[#008A8D] border-gray-200 rounded-full animate-spin"></div></div>;
  if (error || !data) return <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6"><div className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center"><p className="text-red-600 font-medium">{error || 'Lien invalide'}</p></div></div>;

  if (confirmation) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-gradient-to-br from-[#008A8D] to-[#006B6E] text-white py-10 px-6">
          <div className="max-w-3xl mx-auto flex items-center gap-4">
            <img src="/realease-square-beige.jpg" alt="RealEase" className="w-16 h-16 rounded-lg shadow-lg object-contain" />
            <div><h1 className="text-2xl font-bold tracking-wider">REALEASE</h1></div>
          </div>
        </div>
        <div className="max-w-2xl mx-auto p-6">
          <div className="bg-white rounded-xl shadow-md p-8 text-center">
            <div className="w-14 h-14 mx-auto mb-3 bg-green-100 rounded-full flex items-center justify-center">
              <svg className="w-7 h-7 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">RDV confirme !</h2>
            <p className="text-gray-600 mb-1">avec {confirmation.team_member}</p>
            <p className="text-gray-900 font-semibold text-lg mb-6">{selectedSlot?.label_local}</p>
            <p className="text-sm text-gray-500 mb-6">Vous allez recevoir un email de confirmation et une invitation Google Agenda.</p>
            {confirmation.meet_link && <a href={confirmation.meet_link} target="_blank" rel="noreferrer" className="realease-btn-primary inline-block">Lien de la visioconference</a>}
          </div>
        </div>
      </div>
    );
  }

  const roleLabels = { carla: 'Conseillere CRM', julie: 'Pilote de projet', amelie: 'Suivi des delais', ceo: 'CEO', admin: 'Equipe' };
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-gradient-to-br from-[#008A8D] to-[#006B6E] text-white py-10 px-6">
        <div className="max-w-3xl mx-auto flex items-center gap-4">
          <img src="/realease-square-beige.jpg" alt="RealEase" className="w-16 h-16 rounded-lg shadow-lg object-contain" />
          <div>
            <h1 className="text-2xl font-bold tracking-wider">REALEASE</h1>
            <p className="text-sm opacity-90">Prendre rendez-vous avec {data.user.name} ({roleLabels[data.user.role] || data.user.role})</p>
          </div>
        </div>
      </div>
      <div className="max-w-3xl mx-auto p-6">
        <div className="bg-white rounded-xl shadow-md p-8">
          {!selectedSlot ? (
            <>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Choisissez un creneau</h2>
              {Object.keys(slotsByDay).length === 0 ? (
                <p className="text-gray-500">Aucun creneau disponible pour le moment.</p>
              ) : (
                <div className="space-y-5 max-h-[500px] overflow-auto pr-2">
                  {Object.entries(slotsByDay).map(([day, slots]) => (
                    <div key={day}>
                      <h3 className="font-medium text-gray-700 mb-2 capitalize">{day}</h3>
                      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                        {slots.map((s, i) => (
                          <button key={i} onClick={() => setSelectedSlot(s)} className="px-3 py-2 border border-gray-200 rounded-md text-sm hover:border-[#008A8D] hover:bg-[#008A8D]/5" data-testid={`slot-${s.start}`}>
                            {s.label_local.split(' - ')[1]}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <>
              <button onClick={() => setSelectedSlot(null)} className="text-[#008A8D] text-sm mb-3 hover:underline">{'<'} Changer de creneau</button>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Confirmer le RDV</h2>
              <p className="text-gray-600 mb-6">Le <b>{selectedSlot.label_local}</b> avec {data.user.name}</p>
              <div className="grid grid-cols-2 gap-4">
                <FField label="Nom complet *" type="text" value={form.client_name} onChange={v => setForm({...form, client_name: v})} />
                <FField label="Email *" type="email" value={form.client_email} onChange={v => setForm({...form, client_email: v})} />
                <FField label="Telephone" type="tel" value={form.client_phone} onChange={v => setForm({...form, client_phone: v})} />
                <div className="col-span-2"><label className="realease-label">Message / Objet</label><textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} className="realease-input h-20 resize-none" placeholder="Quelques mots sur votre projet..." /></div>
              </div>
              <button onClick={submit} disabled={confirming} className="realease-btn-primary mt-6" data-testid="confirm-booking">{confirming ? 'Confirmation...' : 'Confirmer le rendez-vous'}</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

function App() {
  // Applique le theme sauvegarde au boot (avant tout rendu)
  useEffect(() => {
    try {
      const t = localStorage.getItem('realease_theme') || 'system';
      const resolved = t === 'system'
        ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
        : t;
      const root = document.documentElement;
      if (resolved === 'dark') root.classList.add('dark'); else root.classList.remove('dark');
      // Ecoute les changements systeme si mode "system"
      if (t === 'system' && window.matchMedia) {
        const mql = window.matchMedia('(prefers-color-scheme: dark)');
        const handler = (e) => {
          if (e.matches) root.classList.add('dark'); else root.classList.remove('dark');
        };
        mql.addEventListener?.('change', handler);
        return () => mql.removeEventListener?.('change', handler);
      }
    } catch {}
  }, []);
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <InstallPWAPrompt />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
